import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { strToU8, zipSync } from "fflate";
import { createLeadsPdf } from "./pdf/leads";
import {
  BarChart3,
  BookOpen,
  Brain,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  HeartHandshake,
  Library,
  LockKeyhole,
  Mail,
  Menu,
  Music,
  Sparkles,
  Target,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import raw from "./data/dons_em_acao_dataset.json";
import {
  confidence,
  developmentLevel,
  frequencyScale,
  integratedScore,
  type DimensionScores,
} from "./domain/methodology";
import { objectiveQuestion } from "./domain/questionText";
import {
  ExecutionWorkspace,
  InventoryCompletion,
  type PlanTask,
  type Discipler,
} from "./components/InventoryCompletion";
import { addPdfFooter } from "./pdf/footer";

type Tab =
  | "inicio"
  | "quiz"
  | "resultado"
  | "painel"
  | "evidencias"
  | "plano"
  | "diario"
  | "avaliacao"
  | "oportunidades"
  | "comunidade"
  | "perfil"
  | "necessidades"
  | "livros"
  | "curriculo"
  | "redes"
  | "ecos"
  | "editora"
  | "feedback"
  | "admin";
type Question = {
  id: string;
  display_order: number;
  block: number;
  gift_code: string;
  text: string;
  item_within_gift: number;
  reverse_scored?: boolean;
};
type Gift = {
  code: string;
  name: string;
  definition: string;
  next_step: string;
};
type Store = {
  visitor: Record<string, string | boolean>;
  answers: Record<string, number>;
  profile: Record<string, string>;
  startedAt?: number;
};
const reviewerRoleCodes: Record<string, string> = {
  Pastor: "pastor",
  "Líder": "leader",
  Professor: "teacher",
  Mentor: "mentor",
  Colega: "peer",
  "Pessoa Beneficiada": "beneficiary",
  Membro: "member",
  Outros: "other",
};
const reviewerRoleLabels = Object.keys(reviewerRoleCodes);
const excelText = (value: unknown) => String(value ?? "").replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\ufffe\uffff]/g, "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const leadWorkbook = (rows: any[]) => {
  const cell = (value: unknown, style = 0) => `<c t="inlineStr" s="${style}"><is><t>${excelText(value)}</t></is></c>`;
  const leadRows = rows.map((row, index) => `<row r="${index + 4}">${cell(row.participant_name)}${cell(`+55 ${row.whatsapp}`)}${cell(row.email || "")}${cell(new Date(row.created_at).toLocaleString("pt-BR"))}${cell(row.results?.[0]?.name || "")}${cell(`${row.results?.[0]?.score || 0}%`)}</row>`).join("") || `<row r="4"><c t="inlineStr"><is><t>Nenhum lead encontrado no período.</t></is></c></row>`;
  const sheet = `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols><col min="1" max="1" width="34" customWidth="1"/><col min="2" max="2" width="22" customWidth="1"/><col min="3" max="3" width="38" customWidth="1"/><col min="4" max="4" width="24" customWidth="1"/><col min="5" max="5" width="28" customWidth="1"/><col min="6" max="6" width="18" customWidth="1"/></cols><sheetData><row r="1" ht="32" customHeight="1">${cell("RELATÓRIO DE LEADS", 1)}</row><row r="2">${cell(`Atualizado em ${new Date().toLocaleString("pt-BR")} · Total: ${rows.length} lead(s)`, 2)}</row><row r="3">${["Nome completo", "WhatsApp", "E-mail", "Data do cadastro", "Resultado principal", "Compatibilidade"].map(x => cell(x, 3)).join("")}</row>${leadRows}</sheetData><mergeCells count="2"><mergeCell ref="A1:F1"/><mergeCell ref="A2:F2"/></mergeCells><autoFilter ref="A3:F${Math.max(4, rows.length + 3)}"/></worksheet>`;
  const files: Record<string, Uint8Array> = {
    "[Content_Types].xml": strToU8(`<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`),
    "_rels/.rels": strToU8(`<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`),
    "xl/workbook.xml": strToU8(`<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Leads" sheetId="1" r:id="rId1"/></sheets></workbook>`),
    "xl/_rels/workbook.xml.rels": strToU8(`<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`),
    "xl/styles.xml": strToU8(`<?xml version="1.0" encoding="UTF-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Arial"/></font><font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Arial"/></font></fonts><fills count="4"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF6D28D9"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FF7C3AED"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellXfs count="4"><xf fontId="0" fillId="0" borderId="0"/><xf fontId="1" fillId="2" borderId="0" applyFont="1" applyFill="1"><alignment horizontal="center" vertical="center"/></xf><xf fontId="0" fillId="0" borderId="0"><alignment horizontal="center"/></xf><xf fontId="1" fillId="3" borderId="0" applyFont="1" applyFill="1"><alignment horizontal="center"/></xf></cellXfs></styleSheet>`),
    // OOXML requires autoFilter before mergeCells (Excel repairs the reverse).
    "xl/worksheets/sheet1.xml": strToU8(sheet.replace(/(<mergeCells[\s\S]*?<\/mergeCells>)(<autoFilter[^>]*\/>)/, "$2$1")),
  }; return zipSync(files, { level: 6 });
};

const data = raw as typeof raw & { questions: Question[]; gifts: Gift[] };
const key = "dons-em-acao-v2";
const load = (): Store => {
  try {
    return {
      visitor: {},
      answers: {},
      profile: {},
      ...JSON.parse(localStorage.getItem(key) || "{}"),
    };
  } catch {
    return { visitor: {}, answers: {}, profile: {} };
  }
};
const tabs: { id: Tab; label: string; icon: typeof Brain }[] = [
  { id: "inicio", label: "Início", icon: Sparkles },
  { id: "quiz", label: "Inventário de Dons", icon: Brain },
  { id: "evidencias", label: "Execução", icon: FileText },
  { id: "painel", label: "Meu Desenvolvimento", icon: Target },
  { id: "avaliacao", label: "Peça feedback da comunidade", icon: UsersRound },
  { id: "comunidade", label: "Painel da Comunidade", icon: UsersRound },
  { id: "livros", label: "Livros", icon: Library },
  { id: "curriculo", label: "Currículo", icon: UserRound },
  { id: "redes", label: "Redes Sociais", icon: Sparkles },
  { id: "ecos", label: "Ecos da Graça", icon: Music },
  { id: "editora", label: "Editora Liberato", icon: BookOpen },
  { id: "feedback", label: "Sugestões de Melhoria", icon: Mail },
  { id: "admin", label: "Administrador", icon: LockKeyhole },
];
const text = (x: unknown) => String(x || "");
function Button({
  children,
  onClick,
  kind = "primary",
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  kind?: string;
  disabled?: boolean;
}) {
  const label =
    children === "Ir para meu desenvolvimento"
      ? "Ver resultados e Plano de Ação"
      : children;
  return (
    <button className={`button ${kind}`} disabled={disabled} onClick={onClick}>
      {label}
    </button>
  );
}
function Field({
  label,
  value,
  set,
  multi = false,
  type = "text",
  choices = [],
  error,
  preventPaste = false,
  inputMode,
}: {
  label: string;
  value: string;
  set: (v: string) => void;
  multi?: boolean;
  type?: string;
  choices?: string[];
  error?: string;
  preventPaste?: boolean;
  inputMode?: "text" | "email" | "tel" | "numeric" | "decimal" | "search" | "url" | "none";
}) {
  const lower = label.toLowerCase();
  const autocomplete = lower.includes("e-mail")
    ? "email"
    : lower.includes("whatsapp")
      ? "tel"
      : lower.includes("nome")
        ? "name"
        : type === "password"
          ? "new-password"
          : "on";
  return (
    <label className={`field${error ? " field--error" : ""}`}>
      <span>{label}</span>
      {choices.length ? (
        <select value={value} aria-invalid={Boolean(error)} onChange={(e) => set(e.target.value)}>
          <option value="">Selecione uma opção</option>
          {choices.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      ) : multi ? (
        <textarea
          autoComplete="on"
          value={value}
          aria-invalid={Boolean(error)}
          onChange={(e) => set(e.target.value)}
        />
      ) : (
        <input
          autoComplete={autocomplete}
          type={type}
          value={value}
          inputMode={inputMode}
          aria-invalid={Boolean(error)}
          onPaste={preventPaste ? (e) => e.preventDefault() : undefined}
          onDrop={preventPaste ? (e) => e.preventDefault() : undefined}
          onChange={(e) => set(e.target.value)}
        />
      )}
      {error && <small className="field-error" role="alert">{error}</small>}
    </label>
  );
}
function Header({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  const resolvedTitle = title === "Painel da Comunidade" ? "Dashboard" : title;
  const resolvedEyebrow =
    eyebrow === "VISÃO CONSOLIDADA E ANÔNIMA"
      ? "DASHBOARD CONSOLIDADO E ANÔNIMO"
      : eyebrow;
  return (
    <header className="heading">
      <span>{resolvedEyebrow}</span>
      <h1>{resolvedTitle}</h1>
      <p>{children}</p>
    </header>
  );
}
function JourneyGuide({ active, onGo }: { active: Tab; onGo: (tab: Tab) => void }) {
  const guides: Partial<Record<Tab, { step: string; text: string; action?: string; target?: Tab }>> = {
    inicio: { step: "PASSO 1 DE 5", text: "Cadastre-se uma única vez, aceite a LGPD e crie sua senha numérica. Em seguida, o Inventário será aberto automaticamente.", action: "Começar pelo cadastro", target: "inicio" },
    quiz: { step: "PASSO 2 DE 5", text: "Responda com fatos dos últimos seis meses. Salve seu ritmo: nenhum bloco avança sem todas as 20 respostas obrigatórias." },
    painel: { step: "PASSO 5 DE 5", text: "Aqui você acompanha o que foi planejado, o que já foi executado, seus resultados e os próximos passos." , action: "Registrar uma execução", target: "evidencias" },
    evidencias: { step: "PRÁTICA E REGISTRO", text: "Escolha uma tarefa confirmada, descreva o que foi feito e salve. O progresso voltará automaticamente para Meu Desenvolvimento.", action: "Ver Meu Desenvolvimento", target: "painel" },
    avaliacao: { step: "VALIDAÇÃO COM A COMUNIDADE", text: "Primeiro registre sua autoavaliação; depois gere até cinco links para pessoas que acompanharam seu serviço. As médias e gráficos surgem conforme os retornos chegam." },
    comunidade: { step: "LEITURA COLETIVA", text: "Este Dashboard mostra apenas tendências consolidadas e anônimas. Ele não expõe resultados pessoais nem cria ranking espiritual." },
    perfil: { step: "PREFERÊNCIAS DE SERVIÇO", text: "Informe seu papel, experiência e disponibilidade. Estas informações ajudam a tornar as sugestões de serviço mais realistas." },
    necessidades: { step: "OBSERVAR COM CUIDADO", text: "Registre somente fatos gerais e não sensíveis. Em situações urgentes, procure imediatamente os canais humanos e oficiais de proteção." },
    oportunidades: { step: "SERVIÇO SUPERVISIONADO", text: "As oportunidades compatíveis devem ser confirmadas pela liderança local. Use também as áreas sugeridas no resultado como ponto de partida." },
    plano: { step: "ORGANIZAR PARA AGIR", text: "Transforme uma intenção em meta verificável: ação, quantidade, prazo, evidência e pessoa que acompanhará." },
    diario: { step: "REFLETIR E APRENDER", text: "O Diário é opcional e complementa a Execução: registre aprendizagens amplas que não pertencem a uma tarefa específica." },
    resultado: { step: "LEITURA ORIENTATIVA", text: "Leia os resultados como compatibilidades atuais. Confirme-os por prática, frutos, feedback e serviço supervisionado." },
  };
  const guide = guides[active];
  if (!guide) return null;
  return <div className="journey-guide"><Sparkles size={19}/><div><b>{guide.step}</b><span>{guide.text}</span></div>{guide.action && guide.target && guide.target !== active && <Button kind="outline" onClick={() => onGo(guide.target!)}>{guide.action}<ChevronRight /></Button>}</div>;
}
function SiteLogo() {
  return (
    <a
      className="site-logo"
      href="#inicio"
      aria-label="Dons em Ação - página inicial"
    >
      <span className="site-logo-mark">
        <Sparkles size={18} />
        <Brain size={15} />
      </span>
      <span>
        <b>Dons em Ação</b>
        <small>Descoberta e serviço</small>
      </span>
    </a>
  );
}
function JourneyMotivation({
  answered,
  total,
  goals,
  evidence,
  executions,
}: {
  answered: number;
  total: number;
  goals: number;
  evidence: number;
  executions: number;
}) {
  const answeredPercent = total ? Math.round((answered / total) * 100) : 0;
  const milestones = [
    ["Primeiro passo", answered > 0],
    ["Inventário em andamento", answered >= 20],
    ["Inventário concluído", answered >= total && total > 0],
    ["Plano em ação", goals > 0],
    ["Primeira execução", executions > 0],
    ["Evidência prática", evidence > 0],
  ];
  const unlocked = milestones.filter(([, done]) => done).length;
  const message = !answered
    ? "Comece com sinceridade: cada resposta aproxima você de uma forma prática de servir."
    : answered < total
      ? `Você já percorreu ${answeredPercent}% do inventário. Continue com calma e responda a partir de experiências reais.`
      : goals === 0
        ? "Parabéns: você concluiu uma etapa importante. Ajuste e confirme o Plano de Ação antes de começar a execução."
        : executions === 0
          ? "Seu plano está confirmado. Escolha uma tarefa simples, realize-a com propósito e registre sua primeira execução."
          : evidence === 0
            ? "Você já começou a servir. Registre uma evidência ou aprendizado para enxergar seu crescimento com mais clareza."
            : "Excelente constância: cada passo registrado transforma intenção em aprendizado e serviço responsável.";
  return (
    <div className="journey-motivation" aria-label="Seu progresso na jornada">
      <div>
        <Sparkles size={20} />
        <span>JORNADA EM MOVIMENTO</span>
        <strong>{message}</strong>
      </div>
      <p>
        <b>{unlocked}/6 marcos</b> conquistados · sem comparação: seu ritmo é
        valioso.
      </p>
      <div className="milestones">
        {milestones.map(([label, done]) => (
          <span key={String(label)} className={done ? "done" : ""}>
            {done ? "✓" : "○"} {label}
          </span>
        ))}
      </div>
    </div>
  );
}
function Records({ title, items }: { title: string; items: any[] }) {
  return (
    <div className="record-list">
      <h2 className="section-title">{title}</h2>
      {items.length ? (
        items.map((x, i) => (
          <article key={x.id || i}>
            <b>{x.title || x.activity || x.stage || "Registro"}</b>
            <p>{x.outcome || x.description || x.learning || x.status || ""}</p>
          </article>
        ))
      ) : (
        <p className="empty">
          Nenhum registro ainda. Dados de exemplo não são tratados como dados
          reais.
        </p>
      )}
    </div>
  );
}
const reviewFields: Array<[string, string]> = [
  ["clarity", "Clareza"], ["responsibility", "Responsabilidade"],
  ["consistency", "Constância"], ["impact", "Impacto"],
  ["cooperation", "Cooperação"], ["humility", "Humildade"],
  ["preparation", "Preparo"], ["reliability", "Confiabilidade"],
  ["receptivity", "Abertura para orientações"], ["fruits", "Frutos observados"],
];
const reviewRoleNames: Record<string, string> = {
  pastor: "Pastor", leader: "Líder", teacher: "Professor", mentor: "Mentor",
  peer: "Colega", beneficiary: "Pessoa Beneficiada", member: "Membro", other: "Outros",
};
const reviewAverage = (review: any) => Math.round(reviewFields.reduce((sum, [field]) => sum + Number(review[field] || 0), 0) / reviewFields.length * 10) / 10;
const reviewDate = (value: string) => value ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Data não informada";
function FeedbackReviews({ items }: { items: any[] }) {
  const download = (review: any) => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 38, "F");
    pdf.setTextColor(255, 255, 255); pdf.setFontSize(20); pdf.text("DONS ESPIRITUAIS", 16, 18);
    pdf.setFontSize(10); pdf.text("Francisney Liberato | Feedback e Avaliação", 16, 28);
    pdf.setTextColor(16, 42, 67); pdf.setFontSize(15); pdf.text("Avaliação recebida", 16, 54);
    const identity = review.anonymous ? `Avaliação anônima · ${reviewRoleNames[review.reviewer_role] || "Avaliador"}` : review.reviewer_name || reviewRoleNames[review.reviewer_role] || "Avaliador";
    pdf.setFontSize(10); pdf.text(`Avaliador: ${identity}`, 16, 64, { maxWidth: 178 });
    pdf.text(`Recebida em: ${reviewDate(review.created_at)} | Média: ${reviewAverage(review).toFixed(1)}/5`, 16, 72);
    let y = 88;
    reviewFields.forEach(([field, label]) => { pdf.setTextColor(31, 41, 51); pdf.setFontSize(10); pdf.text(label, 16, y); pdf.setFillColor(234, 243, 238); pdf.roundedRect(88, y - 4, 78, 6, 2, 2, "F"); pdf.setFillColor(23, 107, 92); pdf.roundedRect(88, y - 4, 78 * Number(review[field] || 0) / 5, 6, 2, 2, "F"); pdf.text(`${review[field] || 0}/5`, 172, y); y += 11; });
    if (review.comments) { pdf.setTextColor(16, 42, 67); pdf.setFontSize(13); pdf.text("Comentários e sugestões", 16, y + 7); pdf.setTextColor(82, 99, 109); pdf.setFontSize(10); pdf.text(String(review.comments), 16, y + 16, { maxWidth: 178 }); }
    addPdfFooter(pdf, "Feedback recebido para apoiar aprendizado, serviço e desenvolvimento responsável.");
    pdf.save("feedback-avaliacao-dons-espirituais.pdf");
  };
  return <div className="record-list feedback-received"><h2 className="section-title">Avaliações recebidas</h2>{items.length ? items.map((review, index) => { const identity = review.anonymous ? `Avaliação anônima · ${reviewRoleNames[review.reviewer_role] || "Avaliador"}` : review.reviewer_name || reviewRoleNames[review.reviewer_role] || "Avaliador"; return <article key={review.id || index}><div className="feedback-summary"><div><b>{identity}</b><span>Recebida em {reviewDate(review.created_at)}</span></div><strong>{reviewAverage(review).toFixed(1)}<small>/5</small></strong></div><details><summary>Visualizar resultado da avaliação</summary><div className="feedback-scores">{reviewFields.map(([field, label]) => <p key={field}><span>{label}</span><b>{review[field] || 0}/5</b></p>)}</div>{review.comments && <p className="feedback-comment"><b>Comentários:</b> {review.comments}</p>}</details><Button kind="outline" onClick={() => download(review)}>Baixar PDF da avaliação</Button></article>; }) : <p className="empty">Ainda não há avaliações recebidas. Gere um link individual e compartilhe-o com alguém que tenha acompanhado seu serviço.</p>}</div>;
}
function FeedbackComparison({ name, whatsapp, pin, selfReviews, reviews, onSaved }: { name: string; whatsapp: string; pin: string; selfReviews: any[]; reviews: any[]; onSaved: () => void }) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const self = selfReviews[0];
  const external = reviewFields.reduce((result, [field]) => ({ ...result, [field]: reviews.length ? reviews.reduce((sum, item) => sum + Number(item[field] || 0), 0) / reviews.length : 0 }), {} as Record<string, number>);
  const ownAverage = self ? reviewAverage(self) : 0;
  const externalAverage = reviews.length ? Math.round(reviewFields.reduce((sum, [field]) => sum + external[field], 0) / reviewFields.length * 10) / 10 : 0;
  const integrated = self && reviews.length ? Math.round((ownAverage + externalAverage) / 2 * 10) / 10 : 0;
  const save = async () => { const response = await fetch("/api/development", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "self_review", name, whatsapp, pin, ...form }) }); const body = await response.json() as { error?: string }; if (!response.ok) { setNote(body.error || "Não foi possível salvar sua autoavaliação."); return; } setNote("Autoavaliação salva. Compare com os retornos da comunidade à medida que chegarem."); onSaved(); };
  return <section className="validation-comparison"><Header eyebrow="SÍNTESE DE VALIDAÇÃO" title="Minha percepção e a comunidade">Registre sua percepção nos mesmos critérios e acompanhe como ela converge com as avaliações de pessoas que observam seu serviço.</Header><div className="community-metrics validation-metrics"><article><span>Minha autoavaliação</span><strong>{self ? `${ownAverage.toFixed(1)}/5` : "—"}</strong></article><article><span>Média de terceiros</span><strong>{reviews.length ? `${externalAverage.toFixed(1)}/5` : "—"}</strong></article><article><span>Índice integrado</span><strong>{integrated ? `${integrated.toFixed(1)}/5` : "—"}</strong></article><article><span>Avaliações recebidas</span><strong>{reviews.length}/5</strong></article></div>{self && reviews.length ? <div className="community-chart comparison-chart"><h2>Convergência por critério</h2><div className="comparison-legend"><span className="legend-self">Verde — Minha autoavaliação</span><span className="legend-external">Dourado — Média de terceiros</span><span className="legend-integrated">Azul — Média integrada dos dois resultados</span><span className="legend-gap">GAP = Terceiros − Autoavaliação. Positivo: terceiros percebem mais; negativo: você se percebe acima.</span></div>{reviewFields.map(([field, label]) => { const own=Number(self[field] || 0), third=external[field], average=(own+third)/2, gap=third-own, gapNote=gap>0 ? "Terceiros percebem acima" : gap<0 ? "Autoavaliação acima" : "Alinhamento"; return <div className="comparison-row" key={field}><h3>{label}</h3><div className="comparison-bars"><p><span className="legend-self">Minha autoavaliação</span><i className="self" style={{ width: `${own * 20}%` }} /><b>{own.toFixed(1)}/5</b></p><p><span className="legend-external">Média de terceiros</span><i className="external" style={{ width: `${third * 20}%` }} /><b>{third.toFixed(1)}/5</b></p><p><span className="legend-integrated">Média integrada</span><i className="integrated" style={{ width: `${average * 20}%` }} /><b>{average.toFixed(1)}/5</b></p><p><span className={gap >= 0 ? "legend-gap gap-positive" : "legend-gap gap-negative"}>GAP — {gapNote}</span><i className={gap >= 0 ? "gap gap-positive" : "gap gap-negative"} style={{ width: `${Math.abs(gap) * 20}%` }} /><b>{gap > 0 ? "+" : ""}{gap.toFixed(1)}</b></p></div></div>; })}</div> : <div className="callout"><b>Como interpretar esta etapa</b><p>Registre sua autoavaliação e envie links a até cinco pessoas que observaram seu serviço. Quando houver retorno, o sistema mostrará a média externa e os gráficos de convergência.</p></div>}<div className="history-card self-review-form"><h2>Minha autoavaliação complementar</h2><p>Responda com sinceridade, considerando sua prática recente. Esta resposta não substitui o Inventário; ela permite comparação justa com os mesmos critérios da comunidade.</p><div className="form-grid">{reviewFields.map(([field, label]) => <Field key={field} label={label} value={form[field] || ""} choices={["1", "2", "3", "4", "5"]} set={value => setForm(current => ({ ...current, [field]: value }))} />)}</div><Field label="Comentários e aprendizados (Opcional)" value={form.comments || ""} multi set={value => setForm(current => ({ ...current, comments: value }))} /><Button disabled={reviewFields.some(([field]) => !form[field])} onClick={save}>Salvar minha autoavaliação</Button>{note && <p className="notice">{note}</p>}</div></section>;
}
function AdminTools({
  token,
  notice,
}: {
  token: string;
  notice: (message: string) => void;
}) {
  const [form, setForm] = useState({
    whatsapp: "",
    reviewer_role: "Pastor",
    reviewer_name: "",
    expires_in_days: "30",
    anonymous: true,
  });
  const [link, setLink] = useState("");
  const clearDashboard = async () => {
    if (!window.confirm("Limpar somente o Dashboard? Leads, cadastros, resultados individuais, planos e registros serão preservados.")) return;
    const response = await fetch("/api/admin/dashboard-reset", { method: "POST", headers: { authorization: `Bearer ${token}` } });
    const body = (await response.json()) as { error?: string; message?: string };
    notice(response.ok ? (body.message || "Dashboard limpo sem apagar leads.") : (body.error || "Não foi possível limpar o Dashboard."));
  };
  const create = async () => {
    const response = await fetch("/api/admin/invites", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...form, reviewer_role: reviewerRoleCodes[form.reviewer_role] || "other", anonymous: form.anonymous }),
    });
    const body = (await response.json()) as {
      error?: string;
      review_url?: string;
    };
    if (!response.ok) {
      notice(body.error || "Não foi possível criar o convite.");
      return;
    }
    setLink(body.review_url || "");
    notice("Convite 360º criado com link único e expiração.");
  };
  return (
    <>
      <div className="form-grid">
        <Field
          label="WhatsApp do participante"
          value={form.whatsapp}
          set={(v) => setForm({ ...form, whatsapp: v })}
        />
        <Field
          label="Papel do avaliador"
          value={form.reviewer_role}
          choices={reviewerRoleLabels}
          set={(v) => setForm({ ...form, reviewer_role: v })}
        />
        <Field
          label="Nome do avaliador (opcional)"
          value={form.reviewer_name}
          set={(v) => setForm({ ...form, reviewer_name: v })}
        />
        <Field
          label="Expiração em dias (1 a 60)"
          type="number"
          value={form.expires_in_days}
          set={(v) => setForm({ ...form, expires_in_days: v })}
        />
      </div>
      <label className="consent">
        <input
          type="checkbox"
          checked={form.anonymous}
          onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
        />
        <span>Manter a avaliação anônima ao participante.</span>
      </label>
      <Button disabled={!token} onClick={create}>
        Criar convite 360º
      </Button>
      <div className="callout dashboard-reset">
        <b>Limpar dados exibidos no Dashboard</b>
        <p>
          Inicia uma nova contagem no Dashboard. Não apaga leads, cadastros,
          resultados individuais, planos nem registros de execução.
        </p>
        <Button kind="danger" disabled={!token} onClick={clearDashboard}>
          Limpar Dashboard sem apagar leads
        </Button>
      </div>
      {link && (
        <div className="callout">
          <b>Link individual do avaliador</b>
          <p className="hint">
            Envie por canal seguro. Este link é exibido somente agora e será
            inutilizado após o envio.
          </p>
          <input
            readOnly
            value={link}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      )}
    </>
  );
}
function ContactPanel({ purpose }: { purpose: "editora" | "feedback" }) {
  const [form, setForm] = useState({
      full_name: "",
      email: "",
      whatsapp: "",
      message: "",
    }),
    [note, setNote] = useState("");
  const send = async () => {
    const r = await fetch("/api/editora-contact", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...form,
        purpose,
        started_at: new Date(Date.now() - 5000).toISOString(),
      }),
    });
    const b = (await r.json()) as { error?: string };
    setNote(
      r.ok
        ? "Mensagem enviada com sucesso."
        : b.error || "Não foi possível enviar.",
    );
    if (r.ok) setForm({ full_name: "", email: "", whatsapp: "", message: "" });
  };
  return (
    <div className="callout">
      <div className="form-grid">
        <Field
          label="Nome completo"
          value={form.full_name}
          set={(v) => setForm({ ...form, full_name: v })}
        />
        <Field
          label="E-mail"
          type="email"
          value={form.email}
          set={(v) => setForm({ ...form, email: v })}
        />
        <Field
          label="WhatsApp"
          value={form.whatsapp}
          set={(v) => setForm({ ...form, whatsapp: v })}
        />
        <Field
          label="Mensagem"
          multi
          value={form.message}
          set={(v) => setForm({ ...form, message: v })}
        />
      </div>
      <Button onClick={send}>Enviar mensagem</Button>
      {note && <p className="notice">{note}</p>}
    </div>
  );
}
function HistoryAccess({ name }: { name: string }) {
  const [whatsapp, setWhatsapp] = useState(""),
    [pin, setPin] = useState(""),
    [confirm, setConfirm] = useState(""),
    [note, setNote] = useState(""),
    [records, setRecords] = useState<any[]>([]);
  const setup = async () => {
    if (pin !== confirm) {
      setNote("Repita a mesma senha de 6 dígitos.");
      return;
    }
    const r = await fetch("/api/assessment-access", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, whatsapp, pin }),
    });
    const b = (await r.json()) as { error?: string; created?: boolean };
    setNote(
      r.ok
        ? b.created
          ? "Senha criada. Agora consulte seu histórico."
          : "Senha validada. Agora consulte seu histórico."
        : b.error || "Não foi possível criar a senha.",
    );
  };
  const consult = async () => {
    const r = await fetch(
      `/api/assessment-results?whatsapp=${encodeURIComponent(whatsapp)}&pin=${encodeURIComponent(pin)}`,
    );
    const b = (await r.json()) as { error?: string; records?: any[] };
    if (!r.ok) {
      setNote(b.error || "Não foi possível consultar.");
      return;
    }
    setRecords(b.records || []);
    setNote(
      b.records?.length
        ? "Histórico encontrado."
        : "Nenhum resultado encontrado.",
    );
  };
  return (
    <section className="history-section">
      <div className="history-card">
        <h2>Acessar histórico protegido</h2>
        <p>
          Informe o WhatsApp e crie uma senha pessoal de 6 dígitos. Nas próximas
          consultas, use o mesmo número e a mesma senha.
        </p>
        <div className="form-grid">
          <Field label="WhatsApp com DDD" value={whatsapp} set={setWhatsapp} />
          <Field
            label="Senha de 6 dígitos"
            type="password"
            value={pin}
            set={(v) => setPin(v.replace(/\D/g, "").slice(0, 6))}
          />
          <Field
            label="Confirmar senha de 6 dígitos"
            type="password"
            value={confirm}
            set={(v) => setConfirm(v.replace(/\D/g, "").slice(0, 6))}
          />
        </div>
        <div className="pager">
          <Button kind="outline" onClick={setup}>
            Criar ou validar senha
          </Button>
          <Button onClick={consult}>Consultar histórico</Button>
        </div>
        {note && <p className="notice">{note}</p>}
        {records.map((record) => (
          <article className="history-result" key={record.id}>
            <b>
              Resultado de{" "}
              {new Date(record.created_at).toLocaleDateString("pt-BR")}
            </b>
            <p>
              {record.results
                .map((x: any) => `${x.name} (${x.score}%)`)
                .join(" · ")}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
function AccessPin({
  name,
  whatsapp,
  enabled,
  onReady,
}: {
  name: string;
  whatsapp: string;
  enabled: boolean;
  onReady: () => void;
}) {
  const [pin, setPin] = useState(""),
    [confirm, setConfirm] = useState(""),
    [note, setNote] = useState("");
  if (!enabled) return null;
  const save = async () => {
    if (pin !== confirm || pin.length !== 6) {
      setNote("Crie e confirme uma senha numérica de 6 dígitos.");
      return;
    }
    const r = await fetch("/api/assessment-access", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, whatsapp, pin }),
    });
    const b = (await r.json()) as { error?: string };
    if (!r.ok) {
      setNote(b.error || "Não foi possível salvar a senha.");
      return;
    }
    setNote("Senha pessoal criada. Seu inventário está liberado.");
    onReady();
  };
  return (
    <section className="history-section">
      <div className="history-card">
        <h2>Proteja seu acesso</h2>
        <p>
          Crie uma senha apenas com números, de 6 dígitos. Ela será solicitada
          para acessar novamente seu resultado pelo WhatsApp.
        </p>
        <div className="form-grid">
          <Field
            label="Crie sua senha de 6 dígitos"
            type="password"
            value={pin}
            set={(v) => setPin(v.replace(/\D/g, "").slice(0, 6))}
          />
          <Field
            label="Confirme a senha de 6 dígitos"
            type="password"
            value={confirm}
            set={(v) => setConfirm(v.replace(/\D/g, "").slice(0, 6))}
          />
        </div>
        <Button onClick={save}>Criar senha e liberar inventário</Button>
        {note && <p className="notice">{note}</p>}
      </div>
    </section>
  );
}
function InstitutionalActions({ tab }: { tab: Tab }) {
  const actions: Record<string, { label: string; url: string }[]> = {
    livros: [
      {
        label: "Ver livros de Francisney Liberato",
        url: "https://francisney.com.br/livros/",
      },
    ],
    curriculo: [
      {
        label: "Ver currículo completo",
        url: "https://francisney.com.br/sobre/",
      },
    ],
    redes: [{ label: "Site oficial", url: "https://www.francisney.com.br" }],
    ecos: [
      {
        label: "Ouvir no YouTube",
        url: "https://youtube.com/@ecosdagraca7?si=XS4aX-XSCSjNdRao",
      },
      {
        label: "Ouvir no Spotify",
        url: "https://open.spotify.com/playlist/61hayfialh92Rra9LnRWBg?si=afMIs9_vSAyM3r1Opw76rw&pi=VUcn820rTV2zu",
      },
      {
        label: "Seguir no Instagram",
        url: "https://www.instagram.com/ecosdagraca7",
      },
    ],
    editora: [
      {
        label: "Seguir Editora Liberato",
        url: "https://www.instagram.com/editoraliberato/",
      },
      { label: "Falar no WhatsApp", url: "https://wa.me/5565999031061" },
      { label: "Enviar e-mail", url: "mailto:francisneyliberato10@gmail.com" },
    ],
    feedback: [
      {
        label: "Enviar por e-mail",
        url: "mailto:francisneyliberato10@gmail.com?subject=Sugest%C3%A3o%20para%20Dons%20Espirituais",
      },
    ],
  };
  const items = actions[tab] || [];
  return items.length ? (
    <div className="institutional-actions">
      {items.map((item) => (
        <a
          key={item.url}
          href={item.url}
          target={item.url.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
        >
          {item.label}
          <ChevronRight size={16} />
        </a>
      ))}
    </div>
  ) : null;
}
function CommunityPdf({ community }: { community: any }) {
  const print = () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" }),
      top = community.top || [];
    const hub = { goals: top.map((item: any) => ({ id: item.name, title: item.name, progress: item.count, due_on: "" })), diary: [] as any[] };
    pdf.setFillColor(16, 42, 67);
    pdf.rect(0, 0, 210, 38, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.text("DONS ESPIRITUAIS", 16, 18);
    pdf.setFontSize(10);
    pdf.text("Francisney Liberato - Painel consolidado da comunidade", 16, 28);
    pdf.setTextColor(31, 41, 51);
    pdf.setFontSize(16);
    pdf.text("Visão consolidada da plataforma", 16, 53);
    pdf.setFontSize(10);
    pdf.text(
      `Participantes: ${community.participants || 0}   Avaliações: ${community.assessments || 0}   Média: ${community.average || 0}%`,
      16,
      63,
    );
    let y = 83;
    pdf.setFontSize(14);
    pdf.text("Tendências mais registradas", 16, y);
    y += 10;
    const max = Math.max(1, ...top.map((x: any) => x.count));
    top.slice(0, 8).forEach((x: any) => {
      pdf.setFontSize(10);
      pdf.text(x.name, 16, y);
      pdf.setFillColor(234, 243, 238);
      pdf.roundedRect(65, y - 4, 110, 7, 2, 2, "F");
      pdf.setFillColor(23, 107, 92);
      pdf.roundedRect(65, y - 4, (110 * x.count) / max, 7, 2, 2, "F");
      pdf.setTextColor(31, 41, 51);
      pdf.text(String(x.count), 181, y);
      y += 13;
    });
    pdf.addPage();
    pdf.setFillColor(16, 42, 67);
    pdf.rect(0, 0, 210, 38, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.text("DONS ESPIRITUAIS", 16, 18);
    pdf.setFontSize(9.5);
    pdf.text("Francisney Liberato | Leitura gerencial do Dashboard", 16, 28);
    let planY = 52;
    pdf.setTextColor(16, 42, 67);
    pdf.setFontSize(14);
    pdf.text("Dons em destaque no período", 16, planY);
    planY += 10;
    const goals = hub.goals || [];
    if (!goals.length) {
      pdf.setTextColor(82, 99, 109); pdf.setFontSize(10);
      pdf.text("Ainda não há resultados consolidados para este período.", 16, planY);
    }
    goals.forEach((goal: any, index: number) => {
      if (planY > 240) { pdf.addPage(); pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 32, "F"); pdf.setTextColor(255, 255, 255); pdf.setFontSize(17); pdf.text("DONS ESPIRITUAIS", 16, 18); planY = 46; }
      const executions = (hub.diary || []).filter((entry: any) => entry.goal_id === goal.id);
      pdf.setFillColor(248, 250, 249); pdf.roundedRect(16, planY - 5, 178, 19, 2, 2, "F");
      pdf.setTextColor(16, 42, 67); pdf.setFontSize(10);
      pdf.text(`${index + 1}. ${goal.title}`, 20, planY, { maxWidth: 146 });
      pdf.setTextColor(82, 99, 109); pdf.setFontSize(8.2);
      pdf.text(`Ocorrências: ${goal.progress || 0} | Registros de execução vinculados: ${executions.length}`, 20, planY + 7);
      planY += 23;
    });
    if ((hub.diary || []).length) {
      if (planY > 226) { pdf.addPage(); pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 32, "F"); pdf.setTextColor(255, 255, 255); pdf.setFontSize(17); pdf.text("DONS ESPIRITUAIS", 16, 18); planY = 46; }
      pdf.setTextColor(16, 42, 67); pdf.setFontSize(14); pdf.text("Últimos registros de execução", 16, planY); planY += 10;
      (hub.diary || []).slice(0, 4).forEach((entry: any, index: number) => { pdf.setTextColor(31, 41, 51); pdf.setFontSize(9); pdf.text(`${index + 1}. ${entry.activity || "Atividade registrada"} — ${entry.outcome || "resultado não informado"}`, 18, planY, { maxWidth: 174 }); planY += 9; });
    }
    addPdfFooter(
      pdf,
      "Dados agregados e anônimos. Não representam ranking espiritual nem identificam participantes.",
    );
    pdf.save("painel-comunidade-dons-espirituais.pdf");
  };
  return (
    <Button kind="outline" onClick={print}>
      Baixar PDF do painel
    </Button>
  );
}
function DashboardView({ community }: { community: any }) {
  return (
    <section>
      <Header eyebrow="DASHBOARD CONSOLIDADO E ANÔNIMO" title="Dashboard">
        Informações consolidadas exclusivamente dos Inventários de Dons
        Espirituais desta plataforma, sem identificação individual e sem
        comparação espiritual.
      </Header>
      <div className="community-metrics">
        <article>
          <span>Participantes</span>
          <strong>{community.participants}</strong>
        </article>
        <article>
          <span>Inventários concluídos</span>
          <strong>{community.assessments}</strong>
        </article>
        <article>
          <span>Média dos resultados</span>
          <strong>{community.average}%</strong>
        </article>
        <article>
          <span>Dons em destaque</span>
          <strong>{community.top.length}</strong>
        </article>
      </div>
      <div className="community-chart">
        <h2>Dons mais registrados</h2>
        {community.top.length ? (
          community.top.map((item: any) => (
            <div className="bar-row" key={item.name}>
              <span>{item.name}</span>
              <i>
                <b style={{ width: `${Math.max(8, item.count * 12)}%` }} />
              </i>
              <strong>{item.count}</strong>
            </div>
          ))
        ) : (
          <p className="empty">
            Ainda não há Inventários concluídos neste período.
          </p>
        )}
      </div>
      <div className="callout">
        <h2>Origem dos dados</h2>
        <p>
          Este Dashboard considera somente respostas com a estrutura de 100
          itens deste Inventário de Dons Espirituais. Registros de outras
          aplicações não entram nos totais.
        </p>
      </div>
    </section>
  );
}
function AdminWorkspace({ token }: { token: string }) {
  const [from, setFrom] = useState(''); const [fromTime, setFromTime] = useState(''); const [to, setTo] = useState(''); const [toTime, setToTime] = useState(''); const [message, setMessage] = useState('');
  const query = () => { const p = new URLSearchParams(); if (from) p.set('from', `${from}T${fromTime || '00:00'}:00.000Z`); if (to) p.set('to', `${to}T${toTime || '23:59'}:59.999Z`); return p; };
  const fetchRecords = async () => { const r = await fetch(`/api/admin/records?${query()}`, { headers: { authorization: `Bearer ${token}` } }); const b = await r.json() as { error?: string; records?: any[] }; if (!r.ok) throw new Error(b.error || 'Não foi possível consultar os dados.'); return b.records || []; };
  const leadRows = async (daily = false) => {
    const records = await fetchRecords();
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Cuiaba' });
    return daily ? records.filter(x => new Date(x.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Cuiaba' }) === today) : records;
  };
  const downloadCsv = async (daily = false) => {
    try {
      const rows = await leadRows(daily);
      const bytes = leadWorkbook(rows);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url;
      a.download = daily ? 'relatorio-leads-do-dia.xlsx' : 'relatorio-leads-acumulados.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setMessage(`${rows.length} lead(s) exportado(s) em planilha Excel.`);
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Falha na exportação.'); }
  };
  const downloadLeadPdf = async (daily = false) => {
    try {
      const rows = await leadRows(daily);
      createLeadsPdf(rows, daily).save(daily ? 'leads-do-dia.pdf' : 'leads-acumulados.pdf');
      setMessage(`PDF gerado com ${rows.length} lead(s).`);
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Não foi possível gerar o PDF de leads.'); }
  };
  const pdf = async () => { try { const records = await fetchRecords(); const doc = new jsPDF({ unit: 'mm', format: 'a4' }); const totals = new Map<string, number>(); records.forEach(x => (x.results || []).forEach((item: any) => totals.set(item.name, (totals.get(item.name) || 0) + 1))); const top = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8); const average = records.length ? Math.round(records.reduce((sum, x) => sum + Number(x.results?.[0]?.score || 0), 0) / records.length) : 0; doc.setFillColor(16, 42, 67); doc.rect(0, 0, 210, 38, 'F'); doc.setTextColor(255, 255, 255); doc.setFontSize(21); doc.text('DONS ESPIRITUAIS', 16, 18); doc.setFontSize(10); doc.text('Francisney Liberato | Dashboard administrativo consolidado', 16, 28); doc.setTextColor(31, 41, 51); doc.setFontSize(11); doc.text(`Período: ${from || 'início'} até ${to || 'hoje'} | Avaliações: ${records.length} | Participantes: ${new Set(records.map(x => x.whatsapp)).size} | Média: ${average}%`, 16, 52, { maxWidth: 178 }); let y = 72; doc.setFontSize(15); doc.text('Dons mais registrados', 16, y); y += 11; const max = Math.max(1, ...top.map(x => x[1])); top.forEach(([name, count]) => { doc.setFontSize(10); doc.text(name, 16, y); doc.setFillColor(234,243,238); doc.roundedRect(78, y - 4, 92, 7, 2, 2, 'F'); doc.setFillColor(23,107,92); doc.roundedRect(78, y - 4, 92 * count / max, 7, 2, 2, 'F'); doc.setTextColor(31,41,51); doc.text(String(count), 176, y); y += 13; }); doc.setTextColor(16,42,67); doc.setFontSize(14); doc.text('Registros e áreas de serviço', 16, y + 8); y += 18; records.slice(0, 20).forEach((x, i) => { if (y > 250) { doc.addPage(); y = 22; } const areas = x.ministries?.length ? ` | Áreas: ${x.ministries.join(', ')}` : ''; doc.setTextColor(31,41,51); doc.setFontSize(8.5); doc.text(`${i + 1}. ${x.participant_name} — ${x.results?.[0]?.name || 'Sem resultado'} (${x.results?.[0]?.score || 0}%)${areas}`, 16, y, { maxWidth: 178 }); y += areas ? 11 : 7; }); addPdfFooter(doc, 'Dados consolidados do Dashboard. Não representam ranking espiritual e não excluem leads ou históricos individuais.'); doc.save('dashboard-administrativo-dons-espirituais.pdf'); setMessage(`PDF completo do Dashboard gerado com ${records.length} avaliação(ões).`); } catch (e) { setMessage(e instanceof Error ? e.message : 'Falha ao gerar o PDF.'); } };
  return <div className="admin-workspace"><div className="callout"><h2>Planilhas disponíveis</h2><p>Baixe os leads do dia ou a planilha acumulada de todas as avaliações deste inventário.</p><div className="pager"><Button onClick={() => downloadCsv(true)}>Baixar leads do dia (Excel)</Button><Button kind="outline" onClick={() => downloadCsv(false)}>Baixar leads acumulados (Excel)</Button><Button kind="outline" onClick={() => downloadLeadPdf(true)}>Baixar leads do dia (PDF)</Button><Button kind="outline" onClick={() => downloadLeadPdf(false)}>Baixar leads acumulados (PDF)</Button></div></div><div className="callout"><h2>PDF do Dashboard</h2><p>Defina o período. Sem filtros, o PDF considera todos os registros deste sistema.</p><div className="form-grid"><Field label="Data inicial" type="date" value={from} set={setFrom}/><Field label="Horário inicial" type="time" value={fromTime} set={setFromTime}/><Field label="Data final" type="date" value={to} set={setTo}/><Field label="Horário final" type="time" value={toTime} set={setToTime}/></div><Button onClick={pdf}>Baixar PDF do Dashboard</Button></div>{message && <p className="notice">{message}</p>}<h2 className="section-title">Convite de avaliação 360º</h2><AdminTools token={token} notice={setMessage}/></div>
}

function DevelopmentLogin({
  onAccess,
}: {
  onAccess: (whatsapp: string, pin: string) => void;
}) {
  const [whatsapp, setWhatsapp] = useState(""),
    [pin, setPin] = useState(""),
    [note, setNote] = useState("");
  const enter = async () => {
    const r = await fetch(
        `/api/development?whatsapp=${encodeURIComponent(whatsapp)}&pin=${encodeURIComponent(pin)}`,
      ),
      b = (await r.json()) as { error?: string };
    if (!r.ok) {
      setNote(b.error || "Não foi possível acessar.");
      return;
    }
    onAccess(whatsapp, pin);
  };
  return (
    <section className="development-login">
      <Header eyebrow="ACESSO PESSOAL" title="Meu Desenvolvimento">
        Entre com o mesmo WhatsApp informado no inventário e sua senha numérica
        de 6 dígitos para acompanhar sua jornada.
      </Header>
      <div className="history-card">
        <div className="form-grid">
          <Field label="WhatsApp com DDD" value={whatsapp} set={setWhatsapp} />
          <Field
            label="Senha de 6 dígitos"
            type="password"
            value={pin}
            set={(v) => setPin(v.replace(/\D/g, "").slice(0, 6))}
          />
        </div>
        <Button onClick={enter}>Acessar meu desenvolvimento</Button>
        {note && <p className="notice">{note}</p>}
      </div>
    </section>
  );
}
function FeedbackRequest({ initialWhatsapp = "", initialPin = "" }: { initialWhatsapp?: string; initialPin?: string }) {
  const [whatsapp, setWhatsapp] = useState(initialWhatsapp), [pin, setPin] = useState(initialPin), [role, setRole] = useState("Pastor"), [name, setName] = useState(""), [link, setLink] = useState(""), [note, setNote] = useState("");
  const create = async () => { const response = await fetch("/api/reviews/request", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ whatsapp, pin, reviewer_role: reviewerRoleCodes[role] || "other", reviewer_name: name }) }); const body = await response.json() as { error?: string; review_url?: string }; if (!response.ok) { setNote(body.error || "Não foi possível criar o convite."); return; } setLink(body.review_url || ""); setNote("Link criado. Compartilhe apenas com a pessoa escolhida."); };
  const copyLink = async () => { try { await navigator.clipboard.writeText(link); setNote("Link copiado. Agora você pode enviá-lo ao avaliador."); } catch { setNote("Selecione e copie o link manualmente."); } };
  return <section className="feedback-request"><Header eyebrow="FEEDBACK E AVALIAÇÃO" title="Peça um feedback confiável">Convide uma pessoa que observou seu serviço — Pastor, Líder, Mentor, Colega ou Pessoa Beneficiada. O retorno ajuda a confirmar aprendizados e próximos passos.</Header><div className="history-card"><div className="form-grid"><Field label="Seu WhatsApp com DDD" value={whatsapp} set={setWhatsapp}/><Field label="Sua senha de 6 dígitos" type="password" value={pin} set={v => setPin(v.replace(/\D/g, "").slice(0, 6))}/><Field label="Papel do avaliador" value={role} choices={reviewerRoleLabels} set={setRole}/><Field label="Nome do avaliador (Opcional)" value={name} set={setName}/></div><p className="hint">O link expira em 30 dias e não exibe respostas individuais do seu Inventário.</p><Button disabled={whatsapp.replace(/\D/g, "").length < 10 || pin.length !== 6} onClick={create}>Gerar link de feedback<ChevronRight /></Button>{note && <p className="notice">{note}</p>}{link && <div className="callout feedback-link"><b>Link individual do avaliador</b><input readOnly value={link} onFocus={e => e.currentTarget.select()}/><Button onClick={copyLink}>Copiar link</Button><p>Copie este link e envie ao avaliador escolhido.</p></div>}</div></section>;
}
function PreTestGuide({
  profile,
  onSave,
  onComplete,
}: {
  profile: Record<string, string>;
  onSave: (key: string, value: string) => void;
  onComplete: () => void;
}) {
  const configs = [
    {
      title: "Antes do teste: Como você serve hoje?",
      text: "Não é uma prova. Escolha a opção que melhor descreve sua experiência prática até aqui.",
      key: "service_experience",
      choices: [
        "Ainda não tive oportunidade de servir",
        "Já servi em situações pontuais",
        "Sirvo pelo menos uma vez por mês",
        "Sirvo semanalmente ou mais",
      ],
    },
    {
      title: "Antes do teste: Qual necessidade você percebe?",
      text: "Pense na sua igreja, bairro ou comunidade. Essa informação ajudará a sugerir uma experiência segura depois do teste.",
      key: "observed_need",
      choices: [
        "Acolhimento e cuidado de pessoas",
        "Ensino, estudos e orientação",
        "Organização e apoio a atividades",
        "Evangelismo e missão",
        "Outra necessidade concreta",
      ],
    },
    {
      title: "Antes do teste: Você aceita experimentar?",
      text: "O resultado será apenas um ponto de partida. A prática supervisionada ajuda a confirmar o desenvolvimento.",
      key: "service_readiness",
      choices: [
        "Sim, aceito experimentar com supervisão",
        "Talvez, quero conversar antes com uma liderança",
        "Ainda não estou disponível",
      ],
    },
  ] as const;
  const ready = configs.every((item) => Boolean(profile[item.key]));
  return (
    <section className="development-login pretest-guide">
      <Header eyebrow="PREPARAÇÃO RÁPIDA" title="Antes do Inventário">
        Responda estas três perguntas práticas. Elas ajudam a personalizar suas
        sugestões de serviço e não substituem as 100 questões do Inventário.
      </Header>
      <div className="history-card">
        <div className="form-grid">
          {configs.map((item) => (
            <Field
              key={item.key}
              label={item.title.replace("Antes do teste: ", "")}
              value={profile[item.key] || ""}
              choices={[...item.choices]}
              set={(v) => onSave(item.key, v)}
            />
          ))}
        </div>
        <div className="pager">
          <Button disabled={!ready} onClick={onComplete}>
            Começar Inventário
            <ChevronRight />
          </Button>
        </div>
      </div>
    </section>
  );
}
function DevelopmentPdf({
  name,
  results,
  hub,
  profile,
}: {
  name: string;
  results: any[];
  hub: any;
  profile: Record<string, string>;
}) {
  const print = () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setFillColor(16, 42, 67);
    pdf.rect(0, 0, 210, 38, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(21);
    pdf.text("DONS ESPIRITUAIS", 16, 18);
    pdf.setFontSize(10);
    pdf.text(
      "Francisney Liberato | Relatório de desenvolvimento e plano de ação",
      16,
      28,
    );
    pdf.setTextColor(31, 41, 51);
    pdf.setFontSize(13);
    pdf.text(`Participante: ${name || "Participante"}`, 16, 50);
    pdf.setFontSize(10);
    pdf.text(
      `Experiência atual: ${profile.service_experience || "não informada"}`,
      16,
      58,
      { maxWidth: 175 },
    );
    pdf.text(
      `Necessidade observada: ${profile.observed_need || "não informada"}`,
      16,
      65,
      { maxWidth: 175 },
    );
    let y = 80;
    pdf.setFontSize(15);
    pdf.text("Compatibilidades atuais e próximos passos", 16, y);
    y += 11;
    results.slice(0, 5).forEach((item: any, index: number) => {
      pdf.setFontSize(10);
      pdf.text(`${index + 1}. ${item.gift.name}`, 16, y);
      pdf.setFillColor(234, 243, 238);
      pdf.roundedRect(68, y - 4, 95, 7, 2, 2, "F");
      pdf.setFillColor(23, 107, 92);
      pdf.roundedRect(
        68,
        y - 4,
        Math.max(3, (95 * item.score) / 100),
        7,
        2,
        2,
        "F",
      );
      pdf.setTextColor(31, 41, 51);
      pdf.text(`${item.score}% - ${item.level}`, 168, y);
      y += 13;
    });
    y += 3;
    pdf.setFontSize(14);
    pdf.text("Acompanhamento", 16, y);
    y += 9;
    pdf.setFontSize(10);
    pdf.text(
      `Metas abertas: ${hub.goals?.filter((item: any) => item.status !== "completed").length || 0} | Atividades registradas: ${hub.diary?.length || 0} | Feedbacks: ${hub.reviews?.length || 0}`,
      16,
      y,
      { maxWidth: 178 },
    );
    y += 12;
    pdf.setFontSize(13);
    pdf.text("Ministérios e áreas escolhidos para experimentar", 16, y);
    y += 8;
    const selected = (hub.goals || []).filter((goal: any) => String(goal.title || "").startsWith("Conhecer e experimentar:"));
    pdf.setFontSize(9);
    if (selected.length) selected.forEach((goal: any, index: number) => { pdf.text(`${index + 1}. ${String(goal.title).replace("Conhecer e experimentar: ", "")}`, 18, y, { maxWidth: 170 }); y += 6; });
    else pdf.text("Nenhuma área adicional foi indicada pelo participante.", 18, y);
    const disciplers = (() => { try { return JSON.parse(hub.plans?.[0]?.disciplers_json || "[]") as Array<{ name?: string; church_role?: string }>; } catch { return []; } })();
    if (disciplers.length) { y += 13; pdf.setTextColor(16, 42, 67); pdf.setFontSize(13); pdf.text("Discipuladores indicados", 16, y); y += 7; pdf.setTextColor(82, 99, 109); pdf.setFontSize(9); disciplers.forEach((item, index) => { pdf.text(`${index + 1}. ${item.name || "Nome não informado"} — ${item.church_role || "Função não informada"}`, 18, y, { maxWidth: 172 }); y += 6; }); }
    addPdfFooter(
      pdf,
      "Relatório indicativo. O desenvolvimento dos dons é confirmado pela prática, pelos frutos e pela comunidade.",
    );
    pdf.addPage();
    pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 38, "F");
    pdf.setTextColor(255, 255, 255); pdf.setFontSize(20); pdf.text("DONS ESPIRITUAIS", 16, 18);
    pdf.setFontSize(9.5); pdf.text("Francisney Liberato | Plano confirmado e acompanhamento", 16, 28);
    let planY = 52;
    pdf.setTextColor(16, 42, 67); pdf.setFontSize(14); pdf.text("Planejamento x execução", 16, planY); planY += 10;
    (hub.goals || []).forEach((goal: any, index: number) => {
      if (planY > 240) { pdf.addPage(); pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 32, "F"); pdf.setTextColor(255, 255, 255); pdf.setFontSize(17); pdf.text("DONS ESPIRITUAIS", 16, 18); planY = 46; }
      const executions = (hub.diary || []).filter((entry: any) => entry.goal_id === goal.id);
      pdf.setFillColor(248, 250, 249); pdf.roundedRect(16, planY - 5, 178, 19, 2, 2, "F");
      pdf.setTextColor(16, 42, 67); pdf.setFontSize(10); pdf.text(`${index + 1}. ${goal.title}`, 20, planY, { maxWidth: 146 });
      pdf.setTextColor(82, 99, 109); pdf.setFontSize(8.2); pdf.text(`Prazo: ${goal.due_on ? new Date(`${goal.due_on}T12:00:00`).toLocaleDateString("pt-BR") : "a definir"} | Progresso: ${goal.progress || 0}% | Registros: ${executions.length}`, 20, planY + 7);
      planY += 23;
    });
    if (!(hub.goals || []).length) { pdf.setTextColor(82, 99, 109); pdf.setFontSize(10); pdf.text("O Plano de Ação ainda não foi confirmado.", 16, planY); }
    if ((hub.reviews || []).length || (hub.selfReviews || []).length) {
      pdf.addPage();
      pdf.setFillColor(16, 42, 67); pdf.rect(0, 0, 210, 38, "F");
      pdf.setTextColor(255, 255, 255); pdf.setFontSize(20); pdf.text("DONS ESPIRITUAIS", 16, 18);
      pdf.setFontSize(9.5); pdf.text("Francisney Liberato | Feedbacks recebidos", 16, 28);
      let reviewY = 52;
      pdf.setTextColor(16, 42, 67); pdf.setFontSize(14); pdf.text("Feedbacks e avaliações", 16, reviewY); reviewY += 9;
      const selfReview = hub.selfReviews?.[0]; const receivedAverage = (hub.reviews || []).length ? Math.round((hub.reviews || []).reduce((sum: number, review: any) => sum + reviewAverage(review), 0) / hub.reviews.length * 10) / 10 : 0;
      pdf.setTextColor(82, 99, 109); pdf.setFontSize(9); pdf.text(`Minha autoavaliação: ${selfReview ? `${reviewAverage(selfReview).toFixed(1)}/5` : "não registrada"} | Média de terceiros: ${receivedAverage ? `${receivedAverage.toFixed(1)}/5` : "ainda não disponível"}`, 16, reviewY, { maxWidth: 178 }); reviewY += 10;
      (hub.reviews || []).forEach((review: any, index: number) => { if (reviewY > 238) { pdf.addPage(); reviewY = 22; } const identity = review.anonymous ? `Avaliação anônima · ${reviewRoleNames[review.reviewer_role] || "Avaliador"}` : review.reviewer_name || reviewRoleNames[review.reviewer_role] || "Avaliador"; pdf.setFontSize(10); pdf.setTextColor(16, 42, 67); pdf.text(`${index + 1}. ${identity}`, 16, reviewY); pdf.setTextColor(82, 99, 109); pdf.setFontSize(8.5); pdf.text(`Recebida em ${reviewDate(review.created_at)} | Média: ${reviewAverage(review).toFixed(1)}/5`, 16, reviewY + 5); if (review.comments) pdf.text(String(review.comments), 16, reviewY + 10, { maxWidth: 175 }); reviewY += review.comments ? 19 : 12; });
    }
    addPdfFooter(pdf, "Relatório indicativo. O desenvolvimento dos dons é confirmado pela prática, pelos frutos e pela comunidade.");
    pdf.save("meu-desenvolvimento-dons-espirituais.pdf");
  };
  return (
    <Button kind="outline" onClick={print}>
      Baixar PDF profissional
    </Button>
  );
}
function DevelopmentOverview({
  hub,
  localResults,
  onExecution,
}: {
  hub: any;
  localResults: any[];
  onExecution: () => void;
}) {
  const goals = hub.goals || [],
    diary = hub.diary || [],
    savedResults = hub.assessmentHistory?.[0]?.results || localResults,
    completed = goals.filter((goal: any) => goal.status === "completed").length,
    progress = goals.length
      ? Math.round(
          goals.reduce(
            (sum: number, goal: any) => sum + Number(goal.progress || 0),
            0,
          ) / goals.length,
        )
      : 0;
  return (
    <section>
      <Header eyebrow="ACOMPANHAMENTO INDIVIDUAL" title="Meu Desenvolvimento">
        Aqui você visualiza o resultado salvo da sua avaliação, o Plano de Ação
        confirmado e o que já foi executado.
      </Header>
      <div className="community-metrics">
        <article>
          <span>Tarefas do plano</span>
          <strong>{goals.length}</strong>
        </article>
        <article>
          <span>Tarefas concluídas</span>
          <strong>{completed}</strong>
        </article>
        <article>
          <span>Execuções registradas</span>
          <strong>{diary.length}</strong>
        </article>
        <article>
          <span>Progresso do plano</span>
          <strong>{progress}%</strong>
        </article>
      </div>
      <div className="community-chart">
        <h2>Resultado da última avaliação</h2>
        {savedResults.length ? (
          savedResults.slice(0, 5).map((item: any) => (
            <div className="bar-row" key={item.code || item.name}>
              <span>{item.name}</span>
              <i>
                <b style={{ width: `${item.score || 0}%` }} />
              </i>
              <strong>{item.score || 0}%</strong>
            </div>
          ))
        ) : (
          <p className="empty">
            Seu resultado será exibido aqui após a conclusão do Inventário.
          </p>
        )}
      </div>
      <h2 className="section-title">Planejamento x executado</h2>
      <div className="plan-status">
        {goals.length ? (
          goals.map((goal: any) => {
            const executions = diary.filter(
              (entry: any) => entry.goal_id === goal.id,
            );
            return (
              <article key={goal.id}>
                <b>{goal.title}</b>
                <span>
                  Planejado para:{" "}
                  {goal.due_on
                    ? new Date(`${goal.due_on}T12:00:00`).toLocaleDateString(
                        "pt-BR",
                      )
                    : "a definir"}{" "}
                  · executado: {executions.length} registro(s) · progresso:{" "}
                  {goal.progress || 0}%
                </span>
              </article>
            );
          })
        ) : (
          <p>
            Confirme o Plano de Ação ao finalizar o Inventário para acompanhar
            as tarefas aqui.
          </p>
        )}
      </div>
      <p className="hint">
        Use a aba Execução para registrar cada atividade. O progresso e os
        registros retornam automaticamente a este painel.
      </p>
      <Button onClick={onExecution}>
        Ir para Execução
        <ChevronRight />
      </Button>
    </section>
  );
}
function ExecutionPdf({
  name,
  goals,
  diary,
  reviews,
  plans,
}: {
  name: string;
  goals: any[];
  diary: any[];
  reviews: any[];
  plans: any[];
}) {
  const print = () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pageHeader = (continued = false) => {
      pdf.setFillColor(16, 42, 67);
      pdf.rect(0, 0, 210, 32, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.text("DONS ESPIRITUAIS", 16, 17);
      pdf.setFontSize(9);
      pdf.text(
        continued
          ? "Execução do Plano de Ação - continuação"
          : "Francisney Liberato | Execução do Plano de Ação",
        16,
        26,
      );
    };
    pageHeader();
    pdf.setTextColor(31, 41, 51);
    pdf.setFontSize(12);
    pdf.text(`Participante: ${name || "Participante"}`, 16, 45);
    const disciplers = (() => { try { return JSON.parse(plans?.[0]?.disciplers_json || "[]") as Array<{ name?: string; church_role?: string }>; } catch { return []; } })();
    if (disciplers.length) { pdf.setFontSize(9); pdf.setTextColor(82, 99, 109); pdf.text(`Discipuladores: ${disciplers.map(item => `${item.name || "Nome não informado"} (${item.church_role || "Função não informada"})`).join(" · ")}`, 16, 52, { maxWidth: 178 }); }
    let y = disciplers.length ? 66 : 58;
    const ensure = () => {
      if (y < 246) return;
      pdf.addPage();
      pageHeader(true);
      y = 45;
    };
    pdf.setFontSize(14);
    pdf.text("Tarefas confirmadas", 16, y);
    y += 10;
    goals.forEach((goal, index) => {
      ensure();
      pdf.setFontSize(10);
      pdf.setTextColor(16, 42, 67);
      pdf.text(`${index + 1}. ${goal.title}`, 16, y, { maxWidth: 150 });
      pdf.setTextColor(82, 99, 109);
      pdf.text(
        `Prazo: ${goal.due_on ? new Date(`${goal.due_on}T12:00:00`).toLocaleDateString("pt-BR") : "a definir"} | Progresso: ${goal.progress || 0}%`,
        16,
        y + 6,
      );
      y += 14;
    });
    ensure();
    pdf.setTextColor(16, 42, 67);
    pdf.setFontSize(14);
    pdf.text("Registros de execução", 16, y);
    y += 10;
    if (!diary.length) {
      pdf.setFontSize(10);
      pdf.setTextColor(82, 99, 109);
      pdf.text(
        "Nenhuma execução registrada até o momento. O primeiro passo pode ser simples e supervisionado.",
        16,
        y,
        { maxWidth: 175 },
      );
    }
    diary.forEach((entry, index) => {
      ensure();
      pdf.setTextColor(16, 42, 67);
      pdf.setFontSize(10);
      pdf.text(
        `${index + 1}. ${entry.activity || "Atividade registrada"}`,
        16,
        y,
        { maxWidth: 175 },
      );
      pdf.setTextColor(82, 99, 109);
      pdf.setFontSize(8.5);
      pdf.text(
        `Data: ${entry.occurred_on || "não informada"} | Pessoas: ${entry.people_served || 0} | Tempo: ${entry.minutes_served || 0} min`,
        16,
        y + 5,
      );
      const summary = [entry.outcome, entry.feedback, entry.learning]
        .filter(Boolean)
        .join(" | ");
      if (summary) {
        pdf.text(summary, 16, y + 10, { maxWidth: 175 });
        y += 6;
      }
      y += 12;
    });
    if (reviews.length) {
      pdf.addPage(); pageHeader(true); y = 48;
      pdf.setTextColor(16, 42, 67); pdf.setFontSize(14); pdf.text("Feedbacks recebidos", 16, y); y += 10;
      reviews.forEach((review, index) => { ensure(); const identity = review.anonymous ? `Avaliação anônima · ${reviewRoleNames[review.reviewer_role] || "Avaliador"}` : review.reviewer_name || reviewRoleNames[review.reviewer_role] || "Avaliador"; pdf.setFontSize(10); pdf.setTextColor(16, 42, 67); pdf.text(`${index + 1}. ${identity}`, 16, y); pdf.setTextColor(82, 99, 109); pdf.setFontSize(8.5); pdf.text(`Recebida em ${reviewDate(review.created_at)} | Média: ${reviewAverage(review).toFixed(1)}/5`, 16, y + 5); if (review.comments) pdf.text(String(review.comments), 16, y + 10, { maxWidth: 175 }); y += review.comments ? 19 : 12; });
    }
    addPdfFooter(
      pdf,
      "Plano e execução registrados. Continue servindo com propósito, constância e abertura para aprender.",
    );
    pdf.save("execucao-plano-de-acao-dons-espirituais.pdf");
  };
  return (
    <Button kind="outline" onClick={print}>
      Baixar PDF completo da execução
    </Button>
  );
}

export default function App() {
  const [store, setStore] = useState<Store>(load),
    [active, setActive] = useState<Tab>(() => {
      const saved = load();
      if (saved.visitor.accessGranted !== true) return "inicio";
      return saved.profile.planConfirmed === "yes" ? "painel" : "quiz";
    }),
    [block, setBlock] = useState(() => {
      const answered = Object.keys(load().answers || {}).length;
      return Math.min(5, Math.floor(answered / 20) + 1);
    }),
    [menu, setMenu] = useState(false),
    [notice, setNotice] = useState(""),
    [accessErrors, setAccessErrors] = useState<Record<string, string>>({}),
    [form, setForm] = useState<Record<string, string>>({}),
    [hub, setHub] = useState<any>({
      goals: [],
      diary: [],
      evidence: [],
      reviews: [],
    }),
    [community, setCommunity] = useState<any>({
      participants: 0,
      assessments: 0,
      average: 0,
      top: [],
    }),
    [token, setToken] = useState("");
  const [developmentAccess, setDevelopmentAccess] = useState<{
    whatsapp: string;
    pin: string;
  } | null>(null);
  const [pendingProtectedTab, setPendingProtectedTab] = useState<Tab | null>(null);
  const startNewInventory = () => {
    setStore((current) => ({
      ...current,
      answers: {},
      startedAt: Date.now(),
      profile: { ...current.profile, pretestDone: "" },
    }));
    setBlock(1);
    setNotice(
      "Novo teste iniciado. Responda novamente a preparação e o Inventário com base no seu momento atual.",
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const access = store.visitor.accessGranted === true,
    planConfirmed = store.profile.planConfirmed === "yes",
    answered = Object.keys(store.answers).length,
    complete = answered === data.questions.length;
  const hasConfirmedPlan =
    planConfirmed ||
    (hub.goals?.length || 0) > 0 ||
    Boolean(store.profile.confirmedGoals);
  const openInventoryAfterRegistration = (
    message = "Acesso liberado! Vamos começar o Inventário de Dons.",
  ) => {
    // Persist before navigating.  React state updates are asynchronous and some
    // mobile browsers used to render the old Início screen before this write.
    const nextStore: Store = {
      ...store,
      visitor: {
        ...store.visitor,
        accessGranted: true,
        pinReady: true,
      },
    };
    localStorage.setItem(key, JSON.stringify(nextStore));
    setStore(nextStore);
    setBlock(1);
    setActive("quiz");
    setMenu(false);
    setNotice(message);
    // Assigning the hash dispatches the same navigation event on Chrome,
    // Safari and desktop browsers. replaceState does not dispatch hashchange.
    if (location.hash !== "#quiz") location.hash = "quiz";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const change = (
    part: "visitor" | "profile",
    name: string,
    value: string | boolean,
  ) => setStore((s) => ({ ...s, [part]: { ...s[part], [name]: value } }));
  useEffect(() => localStorage.setItem(key, JSON.stringify(store)), [store]);
  useEffect(() => {
    const h = () => {
      const target = (location.hash.slice(1) as Tab) || "inicio";
      setActive(target === "avaliacao" ? "inicio" : target);
    };
    // On a fresh visit, the initial destination is defined by the saved
    // registration state (Inventário on first access, Meu Desenvolvimento later).
    // This avoids an old #inicio hash overriding a returning participant.
    addEventListener("hashchange", h);
    return () => removeEventListener("hashchange", h);
  }, []);
  const loadHub = async () => {
    if (!developmentAccess) return;
    try {
      const query = `whatsapp=${encodeURIComponent(developmentAccess.whatsapp)}&pin=${encodeURIComponent(developmentAccess.pin)}`;
      const [development, history] = await Promise.all([
        fetch(`/api/development?${query}`),
        fetch(`/api/assessment-results?${query}`),
      ]);
      if (development.ok) {
        const next = (await development.json()) as any;
        next.assessmentHistory = history.ok
          ? ((await history.json()) as any).records || []
          : [];
        setHub(next);
      }
    } catch {}
  };
  useEffect(() => {
    history.replaceState(null, "", `#${active}`);
    scrollTo({ top: 0, behavior: "smooth" });
    if (
      developmentAccess &&
      ["painel", "evidencias", "plano", "diario", "avaliacao"].includes(active)
    )
      loadHub();
    if (active === "comunidade")
      fetch("/api/community-dashboard?period=30")
        .then((r) => (r.ok ? r.json() : null))
        .then((x) => x && setCommunity(x))
        .catch(() => undefined);
  }, [active, developmentAccess]);
  const base = useMemo(
    () =>
      data.gifts
        .map((gift) => {
          const qs = data.questions.filter((q) => q.gift_code === gift.code);
          const vals = qs
            .map((q) => store.answers[q.id])
            .filter((v): v is number => v !== undefined && v >= 0);
          const score = vals.length
            ? Math.round(
                (vals.reduce(
                  (s, v, i) => s + (qs[i]?.reverse_scored ? 5 - v : v),
                  0,
                ) /
                  (vals.length * 5)) *
                  100,
              )
            : 0;
          return { gift, self: score };
        })
        .sort((a, b) => b.self - a.self),
    [store.answers],
  );
  const evidence = hub.evidence?.length || 0,
    reviews = hub.reviews?.length || 0;
  const results = useMemo(
    () =>
      base
        .map((x, i) => {
          const dimensions: DimensionScores = {
            autoperception: x.self,
            evidence: evidence ? Math.min(100, 40 + evidence * 10) : undefined,
            impact: evidence ? Math.min(100, 35 + evidence * 8) : undefined,
            community: reviews ? 70 : undefined,
            maturity: Number(store.profile.maturity) || undefined,
          };
          const score = integratedScore(dimensions);
          return {
            ...x,
            dimensions,
            score,
            level: developmentLevel(
              dimensions,
              score,
              i === 0 && evidence >= 6 && reviews >= 2,
            ),
          };
        })
        .sort((a, b) => b.score - a.score),
    [base, evidence, reviews, store.profile.maturity],
  );
  const vals = Object.values(store.answers).filter((x) => x >= 0),
    contradictions = data.questions.filter(
      (q) => q.reverse_scored && store.answers[q.id] >= 4,
    ).length,
    conf = confidence({
      answered,
      total: data.questions.length,
      durationSeconds: store.startedAt
        ? Math.round((Date.now() - store.startedAt) / 1000)
        : undefined,
      contradictions,
      uniformAnswers: vals.length > 15 && new Set(vals).size === 1,
      practicalExamples: evidence,
      communityReviews: reviews,
      convergence: reviews ? 70 : 0,
    });
  useEffect(() => {
    if (!complete || !access) return;
    fetch("/api/assessment-results", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: store.visitor.name,
        whatsapp: store.visitor.whatsapp,
        email: store.visitor.email,
        answers: store.answers,
        results: results.map((x) => ({
          code: x.gift.code,
          name: x.gift.name,
          score: x.score,
        })),
      }),
    }).catch(() => undefined);
  }, [complete, access, store.answers, results]);
  const go = (t: Tab) => {
    if (t === "evidencias" && !planConfirmed) {
      setNotice(
        "Conclua o Inventário e confirme o Plano de Ação para liberar a Execução. Cada etapa prepara a próxima.",
      );
      setActive("quiz");
    } else if (["evidencias", "avaliacao"].includes(t) && !developmentAccess) {
      setPendingProtectedTab(t);
      setNotice(t === "avaliacao" ? "Para ver os feedbacks recebidos, entre com o WhatsApp e a senha de 6 dígitos usados no seu cadastro." : "Para acessar a Execução, entre com o WhatsApp e a senha de 6 dígitos usados no seu cadastro.");
      setActive("painel");
    } else if (t === "quiz" && (!access || store.visitor.pinReady !== true)) {
      setNotice(
        "Para liberar o inventário, preencha os dados, aceite a LGPD e crie sua senha numérica de 6 dígitos.",
      );
      setActive("inicio");
    } else if (t === "painel" && active === "quiz" && complete) {
      setNotice(
        "Inventário concluído. A seguir estão seus resultados, gráficos e o Plano de Ação sugerido.",
      );
      setTimeout(
        () =>
          document
            .querySelector(".inventory-next-step")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        50,
      );
    } else setActive(t);
    setMenu(false);
  };
  const goJourney = (t: Tab, anchor?: string) => {
    go(t);
    if (anchor && complete) setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const save = async (action: string) => {
    const r = await fetch("/api/development", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action,
        name: store.visitor.name,
        whatsapp: store.visitor.whatsapp,
        email: store.visitor.email,
        ...form,
      }),
    });
    const b = (await r.json()) as { error?: string };
    if (!r.ok) {
      setNotice(b.error || "Não foi possível salvar.");
      return;
    }
    setNotice("Registro salvo.");
    setForm({});
    loadHub();
  };
  const confirmPlan = async (tasks: PlanTask[], disciplers: Discipler[]) => {
    const r = await fetch("/api/development", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action: "confirm_plan",
        name: store.visitor.name,
        whatsapp: store.visitor.whatsapp,
        email: store.visitor.email,
        tasks,
        disciplers,
      }),
    });
    const b = (await r.json()) as { error?: string; goals?: any[] };
    if (!r.ok) {
      setNotice(b.error || "Não foi possível confirmar o plano.");
      return false;
    }
    change("profile", "planConfirmed", "yes");
    change("profile", "confirmedGoals", JSON.stringify(b.goals || []));
    setHub((current: any) => ({
      ...current,
      goals: b.goals || current.goals || [],
    }));
    setNotice(
      "Plano de Ação confirmado. As tarefas já estão disponíveis em Execução.",
    );
    loadHub();
    return true;
  };
  const saveExecution = async (payload: Record<string, string>) => {
    const r = await fetch("/api/development", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action: "execution",
        name: store.visitor.name,
        whatsapp: store.visitor.whatsapp,
        email: store.visitor.email,
        ...payload,
      }),
    });
    const b = (await r.json()) as { error?: string };
    if (!r.ok) {
      setNotice(b.error || "Não foi possível salvar a execução.");
      return false;
    }
    setNotice("Execução salva e progresso da tarefa atualizado.");
    loadHub();
    return true;
  };
  const createManualGoal = async (payload: Record<string, string>) => {
    const r = await fetch("/api/development", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action: "goal",
        name: store.visitor.name,
        whatsapp: store.visitor.whatsapp,
        email: store.visitor.email,
        ...payload,
      }),
    });
    const b = (await r.json()) as { error?: string; goal?: any };
    if (!r.ok) {
      setNotice(b.error || "Não foi possível criar a ação manual.");
      return false;
    }
    setHub((current: any) => ({
      ...current,
      goals: [...(current.goals || []), b.goal],
    }));
    const saved = (() => {
      try {
        return JSON.parse(text(store.profile.confirmedGoals) || "[]");
      } catch {
        return [];
      }
    })();
    change("profile", "confirmedGoals", JSON.stringify([...saved, b.goal]));
    setNotice("Ação manual criada e disponível para execução.");
    return true;
  };
  const answer = (q: Question, v: number) => {
    setStore((s) => ({
      ...s,
      startedAt: s.startedAt || Date.now(),
      answers: { ...s.answers, [q.id]: v },
    }));
    document
      .querySelector(`input[name="${q.id}"]`)
      ?.closest(".question")
      ?.classList.remove("needs-answer");
    const questionsInBlock = data.questions.filter(
      (item) => item.block === block,
    );
    const alreadyAnswered = questionsInBlock.filter(
      (item) => item.id === q.id || store.answers[item.id] !== undefined,
    ).length;
    if (alreadyAnswered === questionsInBlock.length) {
      if (block < 5) {
        setNotice(
          `Parabéns: você concluiu o bloco ${block}. Continue com sinceridade; a próxima etapa começa na questão ${block * 20 + 1}.`,
        );
        setTimeout(() => setBlock(block + 1), 180);
      } else {
        setNotice(
          "Parabéns por concluir o Inventário. Agora conheça seus resultados, gráficos e um Plano de Ação possível para os próximos passos.",
        );
      }
    }
  };
  const advanceBlock = () => {
    const missing = data.questions.filter(
      (item) => item.block === block && store.answers[item.id] === undefined,
    );
    if (missing.length) {
      const numbers = missing.map((item) => item.display_order).join(", ");
      setNotice(
        `Antes de avançar, responda a(s) questão(ões): ${numbers}. Você foi direcionado para a primeira pendência.`,
      );
      missing.forEach((item) =>
        document
          .querySelector(`input[name="${item.id}"]`)
          ?.closest(".question")
          ?.classList.add("needs-answer"),
      );
      setTimeout(
        () =>
          document
            .querySelector(`input[name="${missing[0].id}"]`)
            ?.closest(".question")
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        50,
      );
      return;
    }
    setBlock((value) => value + 1);
  };
  const report = () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setFillColor(16, 42, 67);
    pdf.rect(0, 0, 210, 34, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(19);
    pdf.text("Dons em Ação - Relatório orientativo", 16, 20);
    pdf.setTextColor(31, 41, 51);
    pdf.setFontSize(11);
    pdf.text(
      `Participante: ${text(store.visitor.name) || "Participante"}`,
      16,
      45,
    );
    pdf.text(`Índice de confiança: ${conf.level} (${conf.score}%)`, 16, 53);
    let y = 70;
    results.slice(0, 5).forEach((x, i) => {
      pdf.setFillColor(234, 243, 238);
      pdf.roundedRect(16, y - 6, 178, 15, 3, 3, "F");
      pdf.setTextColor(16, 42, 67);
      pdf.text(`${i + 1}. ${x.gift.name}: ${x.score}% - ${x.level}`, 21, y + 3);
      y += 20;
    });
    addPdfFooter(
      pdf,
      "Resultado indicativo, a confirmar pela prática, frutos e comunidade cristã.",
    );
    pdf.save("relatorio-dons-em-acao.pdf");
  };
  const journeyItems = [
    { id: "inicio" as Tab, label: "Início", note: "Comece sua jornada", icon: Sparkles },
    { id: "quiz" as Tab, label: "Fazer Inventário", note: "100 perguntas objetivas", icon: Brain },
    { id: "quiz" as Tab, label: "Meu Resultado", note: complete ? "Veja suas compatibilidades" : "Conclua o inventário para liberar", icon: BarChart3, locked: !complete, anchor: "journey-result" },
    { id: "quiz" as Tab, label: "Áreas de Atuação", note: complete ? "Conheça áreas para experimentar" : "Disponível após o resultado", icon: UsersRound, locked: !complete, anchor: "journey-areas" },
    { id: "quiz" as Tab, label: "Meu Plano de Ação", note: complete ? "Ajuste e confirme suas tarefas" : "Disponível após o resultado", icon: ClipboardList, locked: !complete, anchor: "journey-plan" },
    { id: "evidencias" as Tab, label: "Registrar Execução", note: hasConfirmedPlan ? "Registre o que foi realizado" : "Confirme o Plano para liberar", icon: FileText, locked: !hasConfirmedPlan },
    { id: "painel" as Tab, label: "Meu Desenvolvimento", note: "Acompanhe progresso e resultados", icon: Target },
    { id: "avaliacao" as Tab, label: "Feedback e Avaliação", note: "Peça retorno para evoluir", icon: UsersRound },
    { id: "comunidade" as Tab, label: "Painel da Comunidade", note: "Tendências consolidadas", icon: UsersRound },
  ];
  const nav = (
    <nav className={menu ? "navigation open" : "navigation"}>
      <strong className="brand">
        <Sparkles size={25} />
        <span>
          DONS
          <br />
          ESPIRITUAIS
        </span>
        <small>por Francisney Liberato</small>
      </strong>
      <div className="nav-group journey-group"><p>JORNADA DOS DONS</p>{journeyItems.map((t) => {
          const I = t.icon;
          return (
            <button
              key={t.label}
              className={active === t.id ? "active" : ""}
              title={t.note}
              onClick={() => goJourney(t.id, t.anchor)}
            >
              <I size={17} />
              <span><b>{t.label}</b><small>{t.note}</small></span>{t.locked && <LockKeyhole size={14} />}
            </button>
          );
        })}</div>
      <div className="nav-group"><p>CONHEÇA FRANCISNEY</p>{tabs.filter(t => ["livros","curriculo","redes","ecos","editora","feedback"].includes(t.id)).map((t) => { const I=t.icon; return <button key={t.id} className={active===t.id?"active":""} onClick={() => go(t.id)}><I size={17}/>{t.label}</button>; })}</div>
      <div className="nav-group restricted-group"><p>ÁREA RESTRITA</p>{tabs.filter(t => t.id === "admin").map((t) => { const I=t.icon; return <button key={t.id} className={active===t.id?"active":""} onClick={() => go(t.id)}><I size={17}/>{t.label}</button>; })}</div>
    </nav>
  );
  const top = results.slice(0, 2),
    complement = results.slice(2, 4),
    emergent = results[4];
  const card = (x: any) => (
    <article key={x.gift.code}>
      <div className="score">
        <strong>{x.score}%</strong>
        <span>{x.level}</span>
      </div>
      <h2>{x.gift.name}</h2>
      <p>
        Compatibilidade atual a confirmar pela prática, frutos e comunidade
        cristã.
      </p>
      <ul className="dimension-list">
        {Object.entries(x.dimensions).map(
          ([k, v]) =>
            v !== undefined && (
              <li key={k}>
                {
                  (
                    {
                      autoperception: "Afinidade",
                      evidence: "Evidência",
                      impact: "Impacto",
                      community: "Confirmação",
                      maturity: "Maturidade",
                    } as any
                  )[k]
                }
                : {String(v)}%
              </li>
            ),
        )}
      </ul>
      <p>
        <b>Próximo passo:</b> {x.gift.next_step}
      </p>
    </article>
  );
  const savedGoals = useMemo(() => {
    try {
      return JSON.parse(text(store.profile.confirmedGoals) || "[]");
    } catch {
      return [];
    }
  }, [store.profile.confirmedGoals]);
  const executionGoals = hub.goals?.length ? hub.goals : savedGoals;
  const institutional =
    active === "livros" ? (
      <section>
        <Header eyebrow="CONHEÇA FRANCISNEY" title="Livros">
          Conheça publicações de Francisney Liberato sobre gestão, liderança,
          inteligência emocional, desenvolvimento e espiritualidade.
        </Header>
        <div className="cards">
          <article>
            <BookOpen />
            <h2>Obras e materiais</h2>
            <p>
              Em breve, este espaço reunirá lançamentos, sinopses, links de
              aquisição e conteúdos de apoio da Editora Liberato.
            </p>
          </article>
          <article>
            <Library />
            <h2>Leitura com propósito</h2>
            <p>
              Conhecimento aplicado à liderança, ao serviço público, à educação
              e à vida cristã.
            </p>
          </article>
        </div>
      </section>
    ) : active === "curriculo" ? (
      <section>
        <Header eyebrow="CONHEÇA FRANCISNEY" title="Currículo">
          Auditor do Tribunal de Contas de Mato Grosso, escritor, palestrante,
          professor, coach e mentor.
        </Header>
        <div className="callout">
          <p>
            Mestre em Educação, Doutor Honoris Causa, graduado em Administração,
            Ciências Contábeis, Direito e Economia. Atua há mais de 25 anos em
            ensino, gestão pública, liderança e desenvolvimento profissional.
          </p>
          <p>
            Também é membro da Academia Mundial de Letras, escritor bíblico,
            palestrante e fundador da Editora Liberato e do selo Ecos da Graça.
          </p>
        </div>
      </section>
    ) : active === "ecos" ? (
      <section>
        <Header eyebrow="CONHEÇA FRANCISNEY" title="Ecos da Graça">
          Selo musical dedicado a composições e mensagens de fé, esperança e
          serviço.
        </Header>
        <div className="cards">
          <article>
            <Music />
            <h2>Canções e mensagens</h2>
            <p>
              Conheça o trabalho autoral e as produções publicadas pelo selo
              Ecos da Graça.
            </p>
          </article>
          <article>
            <Sparkles />
            <h2>Contato artístico</h2>
            <p>
              Use a área de sugestões para apresentações, parcerias e
              comunicações relacionadas ao selo.
            </p>
          </article>
        </div>
      </section>
    ) : active === "editora" ? (
      <section>
        <Header eyebrow="EDITORA LIBERATO" title="Publicações e contato">
          Envie proposta, solicitação ou mensagem para a Editora Liberato.
        </Header>
        <ContactPanel purpose="editora" />
      </section>
    ) : active === "feedback" ? (
      <section>
        <Header eyebrow="SUA OPINIÃO IMPORTA" title="Sugestões de Melhoria">
          Compartilhe sugestões para aperfeiçoar a plataforma e os conteúdos.
        </Header>
        <ContactPanel purpose="feedback" />
      </section>
    ) : null;
  return (
    <div className="app">
      <aside>{nav}</aside>
      <header className="mobile">
        <button className="mobile-home" onClick={() => go("inicio")} aria-label="Ir para a página inicial">
          Dons em Ação
        </button>
        <button onClick={() => setMenu(!menu)}>
          {menu ? <X /> : <Menu />}
        </button>
      </header>
      <main>
        <div className="top">
          <div>
            <span>FRANCISNEY LIBERATO</span>
            <strong>Descoberta → Validação → Desenvolvimento</strong>
          </div>
          <SiteLogo />
        </div>
        {notice && <div className="notice">{notice}</div>}
        <JourneyGuide active={active} onGo={go} />
        {active === "inicio" && (
          <section>
            <Header
              eyebrow="JORNADA DE DONS ESPIRITUAIS"
              title="Descubra seus dons e transforme-os em serviço"
            >
              Você responderá a 100 perguntas objetivas sobre fatos dos últimos
              seis meses. Ao concluir, receberá resultados, áreas de serviço
              sugeridas e um Plano de Ação para colocar em prática.
            </Header>
            <div className="cards">
              <article>
                <Brain />
                <h2>100 perguntas objetivas</h2>
                <p>
                  Responda com base em experiências reais. O Inventário é dividido
                  em blocos para você avançar com calma e segurança.
                </p>
              </article>
              <article>
                <UsersRound />
                <h2>Resultado útil e orientado</h2>
                <p>
                  Veja as compatibilidades atuais, gráficos, áreas de serviço
                  recomendadas e áreas que você deseja experimentar.
                </p>
              </article>
              <article>
                <Target />
                <h2>Plano e execução</h2>
                <p>
                  Ajuste e confirme seu Plano de Ação. Depois, registre a execução,
                  os resultados, os aprendizados e os próximos passos.
                </p>
              </article>
            </div>
            <div className="callout access">
              <h2>Libere seu acesso</h2>
              <p>
                Cadastre seus dados uma única vez. Guarde sua senha de 6 dígitos:
                ela será usada com seu WhatsApp para consultar Meu Desenvolvimento.
              </p>
              <div className="form-grid">
                <Field
                  label="Nome completo"
                  value={text(store.visitor.name)}
                  error={accessErrors.name}
                  set={(v) => { change("visitor", "name", v); setAccessErrors((current) => ({ ...current, name: "" })); }}
                />
                <Field
                  label="WhatsApp"
                  value={text(store.visitor.whatsapp)}
                  error={accessErrors.whatsapp}
                  inputMode="tel"
                  set={(v) => { change("visitor", "whatsapp", v); setAccessErrors((current) => ({ ...current, whatsapp: "" })); }}
                />
                <Field
                  label="E-mail"
                  type="email"
                  value={text(store.visitor.email)}
                  error={accessErrors.email}
                  set={(v) => { change("visitor", "email", v); setAccessErrors((current) => ({ ...current, email: "" })); }}
                />
                <div className="access-code-guide" role="status">
                  <span aria-hidden="true">⚠️</span>
                  <strong>Digite o código: Francisney Liberato</strong>
                  <small>Copiar e colar não é aceito.</small>
                </div>
                <Field
                  label="Código de acesso"
                  value={text(store.visitor.code)}
                  error={accessErrors.code}
                  preventPaste
                  set={(v) => { change("visitor", "code", v); setAccessErrors((current) => ({ ...current, code: "" })); }}
                />
                <Field
                  label="Crie sua senha numérica de 6 dígitos"
                  type="password"
                  value={text(store.visitor.pin)}
                  error={accessErrors.pin}
                  inputMode="numeric"
                  set={(v) => { change("visitor", "pin", v.replace(/\D/g, "").slice(0, 6)); setAccessErrors((current) => ({ ...current, pin: "" })); }}
                />
              </div>
              <label className={`consent${accessErrors.lgpd ? " consent--error" : ""}`}>
                <input
                  type="checkbox"
                  checked={store.visitor.lgpd === true}
                  onChange={(e) => { change("visitor", "lgpd", e.target.checked); setAccessErrors((current) => ({ ...current, lgpd: "" })); }}
                />
                <span>
                  <b>Consentimento LGPD</b>
                  <br />
                  Autorizo o tratamento dos meus dados pessoais para o envio de
                  conteúdos, cursos, livros, palestras, mentorias, eventos,
                  comunicações institucionais e demais contatos relacionados à
                  gestão pública, liderança, inteligência emocional,
                  desenvolvimento profissional e outros temas, do palestrante e
                  professor Francisney Liberato, nos termos da legislação
                  aplicável.
                </span>
              </label>
              {accessErrors.lgpd && <p className="field-error consent-error" role="alert">{accessErrors.lgpd}</p>}
              <Button
                disabled={access}
                onClick={async () => {
                  const pin = text(store.visitor.pin);
                  const errors: Record<string, string> = {};
                  if (text(store.visitor.name).trim().length <= 2) errors.name = "Informe seu nome completo.";
                  if (text(store.visitor.whatsapp).replace(/\D/g, "").length < 10) errors.whatsapp = "Informe o WhatsApp com DDD.";
                  if (!/^\S+@\S+\.\S+$/.test(text(store.visitor.email))) errors.email = "Informe um e-mail válido.";
                  if (text(store.visitor.code).replace(/\s/g, "").toLowerCase() !== "francisneyliberato") errors.code = "Código incorreto. Digite Francisney Liberato.";
                  if (!/^\d{6}$/.test(pin)) errors.pin = "Crie uma senha com exatamente 6 números.";
                  if (store.visitor.lgpd !== true) errors.lgpd = "Marque o Consentimento LGPD para liberar seu acesso.";
                  if (Object.keys(errors).length) {
                    setAccessErrors(errors);
                    setNotice("Revise os campos destacados em vermelho antes de liberar o acesso.");
                    return;
                  }
                  setAccessErrors({});
                  const response = await fetch("/api/assessment-access", {
                      method: "POST",
                      headers: { "content-type": "application/json" },
                      body: JSON.stringify({
                        name: store.visitor.name,
                        whatsapp: store.visitor.whatsapp,
                        pin,
                      }),
                    }),
                    body = (await response.json()) as { error?: string };
                  if (!response.ok) {
                    setNotice(
                      body.error || "Não foi possível salvar sua senha.",
                    );
                    return;
                  }
                  openInventoryAfterRegistration();
                }}
              >
                {access ? "Acesso liberado" : "Criar senha e liberar acesso"}
                <ChevronRight />
              </Button>
            </div>
          </section>
        )}
        {active === "quiz" && store.profile.pretestDone === "yes" && (
          <section>
            <Header
              eyebrow={`BLOCO ${block} DE 5 · ÚLTIMOS SEIS MESES`}
              title="Inventário objetivo de experiências"
            >
              Responda por frequência. Cada bloco tem 20 questões obrigatórias;
              depois de responder todas, use “Próximo bloco” para avançar.
            </Header>
            <div className="callout quiz-help"><b>Como responder</b><p>Use a escala de 0 a 5: Nunca, Uma vez, Algumas vezes, Mensalmente, Semanalmente ou Várias vezes por semana. “Nunca tive oportunidade” significa ausência de experiência prática, não ausência de dom.</p><p>Ao tentar avançar, o sistema indica exatamente quais questões ainda precisam de uma escolha.</p></div>
            <div className="progress">
              <i
                style={{
                  width: `${(answered / data.questions.length) * 100}%`,
                }}
              />
              <p>
                {answered} de {data.questions.length} respondidos. Neste bloco:{" "}
                {
                  data.questions.filter(
                    (q) =>
                      q.block === block && store.answers[q.id] !== undefined,
                  ).length
                }
                /20.
              </p>
            </div>
            <div className="questions">
              {data.questions
                .filter((q) => q.block === block)
                .map((q) => (
                  <article className="question" key={q.id}>
                    <p>
                      <b>{q.display_order}.</b>{" "}
                      {objectiveQuestion(
                        q.gift_code,
                        q.item_within_gift - 1,
                        q.text,
                      )}
                    </p>
                    <div>
                      {frequencyScale.map((o) => (
                        <label
                          key={o.value}
                          className={
                            store.answers[q.id] === o.value ? "selected" : ""
                          }
                        >
                          <input
                            disabled={!access}
                            type="radio"
                            name={q.id}
                            checked={store.answers[q.id] === o.value}
                            onChange={() => answer(q, o.value)}
                          />
                          <b>{o.value === -1 ? "—" : o.value}</b>
                          <span>{o.label}</span>
                        </label>
                      ))}
                    </div>
                  </article>
                ))}
            </div>
            <div className="pager">
              <Button
                kind="outline"
                disabled={block === 1}
                onClick={() => setBlock((b) => b - 1)}
              >
                <ChevronLeft />
                Anterior
              </Button>
              {block < 5 ? (
                <Button onClick={advanceBlock}>
                  Próximo bloco
                  <ChevronRight />
                </Button>
              ) : (
                <Button disabled={!complete} onClick={() => go("painel")}>
                  Ir para meu desenvolvimento
                </Button>
              )}
            </div>
          </section>
        )}
        {active === "resultado" && (
          <section>
            <Header eyebrow="LEITURA ORIENTATIVA" title="Meu Resultado">
              O resultado é uma hipótese de desenvolvimento, não uma conclusão
              absoluta.
            </Header>
            {!complete ? (
              <div className="callout">
                Complete o inventário: {answered}/{data.questions.length}.<br />
                <Button onClick={() => go("quiz")}>Continuar</Button>
              </div>
            ) : (
              <>
                <div className="confidence">
                  <Brain />
                  <div>
                    <b>
                      Índice de confiança: {conf.level} ({conf.score}%)
                    </b>
                    <p>{conf.reasons.join(" · ")}</p>
                  </div>
                </div>
                <div className="results">{top.map(card)}</div>
                <h2 className="section-title">Dons complementares</h2>
                <div className="cards compact-cards">
                  {complement.map(card)}
                </div>
                {emergent && (
                  <div className="callout">
                    <b>Dom emergente: {emergent.gift.name}</b>
                    <p>Há sinais que merecem experiência prática e feedback.</p>
                  </div>
                )}
                <div className="pager">
                  <Button onClick={() => go("evidencias")}>
                    Registrar evidências
                  </Button>
                  <Button kind="outline" onClick={report}>
                    Baixar PDF
                  </Button>
                  <Button kind="outline" onClick={() => go("plano")}>
                    Plano de 90 dias
                  </Button>
                </div>
              </>
            )}
          </section>
        )}
        {active === "painel" && (
          <DevelopmentOverview hub={hub} localResults={results} onExecution={() => go("evidencias")} />
        )}
        {active === "evidencias" && (
          <section>
            <Header
              eyebrow="FATOS, FRUTOS E APRENDIZADO"
              title="Evidências do meu desenvolvimento"
            >
              Registre atividade, data, contexto, resultado, dificuldade, pessoa
              que pode confirmar e próximo passo.
            </Header>
            <div className="form-grid">
              <Field
                label="Dom"
                value={form.gift_code || ""}
                choices={results.map((x) => `${x.gift.code} — ${x.gift.name}`)}
                set={(v) => setForm({ ...form, gift_code: v.split(" ")[0] })}
              />
              <Field
                label="Atividade realizada"
                value={form.activity || ""}
                set={(v) => setForm({ ...form, activity: v })}
              />
              <Field
                label="Data aproximada"
                type="date"
                value={form.occurred_on || ""}
                set={(v) => setForm({ ...form, occurred_on: v })}
              />
              <Field
                label="Contexto"
                value={form.context || ""}
                set={(v) => setForm({ ...form, context: v })}
              />
              <Field
                label="Público atendido"
                value={form.audience || ""}
                set={(v) => setForm({ ...form, audience: v })}
              />
              <Field
                label="Resultado observado"
                multi
                value={form.outcome || ""}
                set={(v) => setForm({ ...form, outcome: v })}
              />
              <Field
                label="Dificuldade"
                multi
                value={form.difficulty || ""}
                set={(v) => setForm({ ...form, difficulty: v })}
              />
              <Field
                label="Pessoa que pode confirmar"
                value={form.verifier_name || ""}
                set={(v) => setForm({ ...form, verifier_name: v })}
              />
              <Field
                label="Aprendizado"
                multi
                value={form.learning || ""}
                set={(v) => setForm({ ...form, learning: v })}
              />
              <Field
                label="Próximo passo"
                value={form.next_step || ""}
                set={(v) => setForm({ ...form, next_step: v })}
              />
            </div>
            <Button onClick={() => save("evidence")}>Salvar evidência</Button>
            <Records title="Evidências registradas" items={hub.evidence} />
          </section>
        )}
        {active === "plano" && (
          <section>
            <Header
              eyebrow="PLANO DE AÇÃO PERSONALIZADO"
              title="Plano de 90 dias"
            >
              Prioridade atual:{" "}
              {top.map((x) => x.gift.name).join(" e ") || "concluir inventário"}
              .
            </Header>
            <div className="cards">
              {[
                [
                  "1. COMPREENDER",
                  "Semanas 1 e 2",
                  "Estude o dom, seus limites e converse com líder.",
                ],
                [
                  "2. EXPERIMENTAR",
                  "Semanas 3 a 6",
                  "Execute serviço supervisionado e registre evidências.",
                ],
                [
                  "3. RECEBER FEEDBACK",
                  "Semanas 7 e 8",
                  "Peça retorno de líder, colega e pessoa beneficiada.",
                ],
                [
                  "4. CONSOLIDAR",
                  "Semanas 9 a 12",
                  "Defina rotina, mentor, capacitação e reavaliação.",
                ],
              ].map((x) => (
                <article key={x[0]}>
                  <span>{x[1]}</span>
                  <h2>{x[0]}</h2>
                  <p>{x[2]}</p>
                </article>
              ))}
            </div>
            <Button onClick={() => save("plan")}>Criar meu plano</Button>
            <h2 className="section-title">Meta objetiva</h2>
            <div className="form-grid">
              <Field
                label="Título"
                value={form.title || ""}
                set={(v) => setForm({ ...form, title: v })}
              />
              <Field
                label="Dom"
                value={form.gift_code || ""}
                set={(v) => setForm({ ...form, gift_code: v })}
              />
              <Field
                label="Ação verificável"
                value={form.action_text || ""}
                set={(v) => setForm({ ...form, action_text: v })}
              />
              <Field
                label="Quantidade"
                type="number"
                value={form.quantity || ""}
                set={(v) => setForm({ ...form, quantity: v })}
              />
              <Field
                label="Prazo"
                type="date"
                value={form.due_on || ""}
                set={(v) => setForm({ ...form, due_on: v })}
              />
              <Field
                label="Frequência"
                value={form.frequency || ""}
                set={(v) => setForm({ ...form, frequency: v })}
              />
              <Field
                label="Indicador de resultado"
                value={form.result_indicator || ""}
                set={(v) => setForm({ ...form, result_indicator: v })}
              />
              <Field
                label="Evidência exigida"
                value={form.evidence_required || ""}
                set={(v) => setForm({ ...form, evidence_required: v })}
              />
              <Field
                label="Avaliador"
                value={form.evaluator || ""}
                set={(v) => setForm({ ...form, evaluator: v })}
              />
            </div>
            <Button onClick={() => save("goal")}>Salvar meta</Button>
            <Records title="Minhas metas" items={hub.goals} />
          </section>
        )}
        {active === "diario" && (
          <section>
            <Header
              eyebrow="APRENDER PELA PRÁTICA"
              title="Diário de desenvolvimento"
            >
              Registre data, dom, atividade, contexto, pessoas, tempo,
              resultado, feedback, dificuldades, aprendizado e próximo passo.
            </Header>
            <div className="form-grid">
              <Field
                label="Data"
                type="date"
                value={form.occurred_on || ""}
                set={(v) => setForm({ ...form, occurred_on: v })}
              />
              <Field
                label="Dom"
                value={form.gift_code || ""}
                set={(v) => setForm({ ...form, gift_code: v })}
              />
              <Field
                label="Atividade"
                value={form.activity || ""}
                set={(v) => setForm({ ...form, activity: v })}
              />
              <Field
                label="Contexto"
                value={form.context || ""}
                set={(v) => setForm({ ...form, context: v })}
              />
              <Field
                label="Pessoas atendidas"
                type="number"
                value={form.people_served || ""}
                set={(v) => setForm({ ...form, people_served: v })}
              />
              <Field
                label="Minutos dedicados"
                type="number"
                value={form.minutes_served || ""}
                set={(v) => setForm({ ...form, minutes_served: v })}
              />
              <Field
                label="Resultado"
                multi
                value={form.outcome || ""}
                set={(v) => setForm({ ...form, outcome: v })}
              />
              <Field
                label="Feedback"
                multi
                value={form.feedback || ""}
                set={(v) => setForm({ ...form, feedback: v })}
              />
              <Field
                label="Dificuldades"
                multi
                value={form.difficulties || ""}
                set={(v) => setForm({ ...form, difficulties: v })}
              />
              <Field
                label="Aprendizado"
                multi
                value={form.learning || ""}
                set={(v) => setForm({ ...form, learning: v })}
              />
              <Field
                label="Próximo passo"
                value={form.next_step || ""}
                set={(v) => setForm({ ...form, next_step: v })}
              />
            </div>
            <Button onClick={() => save("diary")}>Salvar diário</Button>
            <Records title="Atividades registradas" items={hub.diary} />
          </section>
        )}
        {active === "avaliacao" && (
          <section>
            <Header
              eyebrow="VALIDAÇÃO COM CUIDADO"
              title="Avaliação comunitária 360º"
            >
              Pastor, líder, professor, mentor, colega, pessoa beneficiada ou
              membro da comunidade avaliam clareza, responsabilidade,
              constância, impacto, cooperação, humildade, preparo,
              confiabilidade, abertura a orientações e frutos.
            </Header>
            <div className="callout">
              <h2>Convites individuais e privados</h2>
              <p>
                Gere até cinco links individuais para pessoas que observam seu
                serviço. Cada convite expira em 30 dias e o resultado aparece
                aqui somente para o participante autenticado.
              </p>
            </div>
            <FeedbackReviews items={hub.reviews || []} />
          </section>
        )}
        {active === "oportunidades" && (
          <section>
            <Header
              eyebrow="SERVIR COM SEGURANÇA"
              title="Oportunidades de serviço"
            >
              Oportunidades têm título, local, data, duração, responsável,
              vagas, habilidades, dons, nível, requisitos, inscrição e status.
              Publicação e inscrição dependem de perfis autorizados.
            </Header>
            <div className="cards">
              <article>
                <h2>Recomendação responsável</h2>
                <p>
                  Compatibilidade atual, disponibilidade e requisitos orientam
                  sugestões; nunca criam ranking espiritual.
                </p>
              </article>
              <article>
                <h2>Segurança</h2>
                <p>
                  Atividades com vulnerabilidade, saúde, menores ou deslocamento
                  exigem supervisão e regras locais.
                </p>
              </article>
            </div>
          </section>
        )}
        {active === "comunidade" && <DashboardView community={community} />}
        {active === "perfil" && (
          <section>
            <Header
              eyebrow="MATURIDADE E DISPONIBILIDADE"
              title="Perfil Ministerial"
            >
              Perguntas fechadas reduzem ambiguidade. Escolher um papel não
              concede permissão administrativa.
            </Header>
            <div className="form-grid">
              <Field
                label="Papel"
                value={store.profile.role || ""}
                choices={[
                  "Participante",
                  "Pastor",
                  "Líder",
                  "Mentor",
                  "Coordenador",
                ]}
                set={(v) => change("profile", "role", v)}
              />
              <Field
                label="Experiência"
                value={store.profile.experience || ""}
                choices={[
                  "Nunca tive oportunidade",
                  "Pontual",
                  "Mensal",
                  "Semanal",
                  "Várias vezes por semana",
                ]}
                set={(v) => change("profile", "experience", v)}
              />
              <Field
                label="Disponibilidade"
                value={store.profile.availability || ""}
                choices={[
                  "Até 2 horas/mês",
                  "3 a 8 horas/mês",
                  "9 a 16 horas/mês",
                  "Mais de 16 horas/mês",
                ]}
                set={(v) => change("profile", "availability", v)}
              />
              <Field
                label="Prontidão para receber feedback"
                value={store.profile.maturity || ""}
                choices={["20", "40", "60", "80", "100"]}
                set={(v) => change("profile", "maturity", v)}
              />
              <p className="hint">Este registro fica salvo somente neste dispositivo como rascunho. Ele não substitui o encaminhamento humano, nem envia dados sensíveis à plataforma.</p>
              <Button onClick={() => { change("profile", "need_draft", JSON.stringify({ title: form.need_title || "", facts: form.need_facts || "", urgency: form.urgency || "" })); setNotice("Rascunho de necessidade salvo neste dispositivo. Converta-o em uma ação prática com a liderança local."); }}>Salvar rascunho</Button>
            </div>
          </section>
        )}
        {active === "necessidades" && (
          <section>
            <Header
              eyebrow="OLHAR ATENTO À COMUNIDADE"
              title="Necessidades percebidas"
            >
              Registre fatos observáveis. Em risco imediato, violência, abuso,
              emergência médica ou ameaça à vida, use canais humanos e oficiais
              de proteção.
            </Header>
            <div className="callout">
              <Field
                label="Título curto"
                value={form.need_title || ""}
                set={(v) => setForm({ ...form, need_title: v })}
              />
              <Field
                label="Fatos observáveis, sem dados sensíveis"
                multi
                value={form.need_facts || ""}
                set={(v) => setForm({ ...form, need_facts: v })}
              />
              <Field
                label="Urgência"
                value={form.urgency || ""}
                choices={[
                  "Baixa",
                  "Moderada",
                  "Alta",
                  "Emergência - não registrar aqui",
                ]}
                set={(v) => setForm({ ...form, urgency: v })}
              />
            </div>
          </section>
        )}
        {active === "redes" && (
          <section>
            <Header eyebrow="CONHEÇA FRANCISNEY" title="Redes sociais">
              Conteúdos e canais oficiais.
            </Header>
            <div className="socials">
              {[
                ["Instagram", "https://www.instagram.com/francisneyliberato/"],
                [
                  "Facebook",
                  "https://www.facebook.com/francisney.liberato.batista.siqueira/",
                ],
                [
                  "YouTube",
                  "https://www.youtube.com/results?search_query=Francisney+Liberato",
                ],
                [
                  "LinkedIn",
                  "https://www.linkedin.com/in/francisneyliberato-batista-siqueira-87871288",
                ],
                [
                  "Kwai",
                  "https://www.kwai.com/search?keyword=Francisney%20Liberato",
                ],
                [
                  "TikTok",
                  "https://www.tiktok.com/search?q=FrancisneyLiberato",
                ],
                ["WhatsApp", "https://wa.me/5565999031061"],
              ].map(([n, u]) => (
                <a key={n} href={u} target="_blank" rel="noreferrer">
                  {n}
                  <ChevronRight />
                </a>
              ))}
            </div>
          </section>
        )}
        {active === "admin" && (
          <section>
            <Header eyebrow="ÁREA RESTRITA" title="Administrador">
              A administração exige segredo de ambiente e autorização no
              servidor.
            </Header>
            <div className="callout">
              <Field
                label="Senha do Administrador"
                type="password"
                value={token}
                set={setToken}
              />
              <Button
                onClick={async () => {
                  const r = await fetch("/api/admin/health", {
                    headers: { authorization: `Bearer ${token}` },
                  });
                  const b = (await r.json()) as {
                    error?: string;
                    participant_profiles?: number;
                    active_invites?: number;
                  };
                  setNotice(
                    r.ok
                      ? `Sessão validada: ${b.participant_profiles} participante(s) e ${b.active_invites} convite(s) ativo(s).`
                      : b.error || "Senha inválida.",
                  );
                }}
              >
                Validar sessão
              </Button>
            </div>
            {token && <AdminWorkspace token={token} />}
            <p className="hint">
              O segredo está configurado apenas no Cloudflare. Não exponha senha
              ou token em mensagens públicas.
            </p>
          </section>
        )}
        {["evidencias", "painel"].includes(active) ||
        (active === "quiz" && store.profile.pretestDone === "yes") ? (
          <JourneyMotivation
            answered={answered}
            total={data.questions.length}
            goals={hub.goals?.length || 0}
            evidence={hub.evidence?.length || 0}
            executions={hub.diary?.length || 0}
          />
        ) : null}
        {active === "quiz" && store.profile.pretestDone !== "yes" && (
          <PreTestGuide
            profile={store.profile}
            onSave={(key, value) => change("profile", key, value)}
            onComplete={() => {
              change("profile", "pretestDone", "yes");
              setNotice("Preparação concluída. Agora responda o Inventário com calma e com base em experiências reais.");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
        {active === "quiz" && complete && (
          <section className="inventory-next-step">
            <Header eyebrow="PRÓXIMA ETAPA" title="Resultado e Plano de Ação">
              Você concluiu o Inventário. A seguir, confira os resultados,
              ajuste o plano sugerido e confirme as tarefas antes de iniciar a
              Execução.
            </Header>
            <Button kind="outline" onClick={startNewInventory}>
              Fazer novo teste
            </Button>
          </section>
        )}
        {active === "quiz" && complete && (
          <InventoryCompletion
            name={text(store.visitor.name)}
            profile={store.profile}
            results={results}
            confidence={conf}
            confirmed={store.profile.planConfirmed === "yes"}
            onConfirm={confirmPlan}
            onExecution={() => go("evidencias")}
            onNotice={setNotice}
          />
        )}
        {active === "evidencias" && (
          <ExecutionWorkspace
            goals={executionGoals}
            onSave={saveExecution}
            onCreateManual={createManualGoal}
            onNotice={setNotice}
          />
        )}
        {active === "evidencias" && hasConfirmedPlan && (
          <div className="pdf-actions execution-pdf">
            <ExecutionPdf
              name={
                text(hub.participant?.display_name) || text(store.visitor.name)
              }
              goals={executionGoals}
              diary={hub.diary || []}
              reviews={hub.reviews || []}
              plans={hub.plans || []}
            />
            <Button onClick={() => go("painel")}>
              Ir para Meu Desenvolvimento
              <ChevronRight />
            </Button>
          </div>
        )}
        {active === "painel" && !developmentAccess && (
          <DevelopmentLogin
            onAccess={(whatsapp, pin) => {
              setDevelopmentAccess({ whatsapp, pin });
              if (pendingProtectedTab) {
                setActive(pendingProtectedTab);
                setPendingProtectedTab(null);
              }
            }}
          />
        )}{" "}
        {active === "painel" && developmentAccess && (
          <div className="pdf-actions">
            <DevelopmentPdf
              name={
                text(hub.participant?.display_name) || text(store.visitor.name)
              }
              results={results}
              hub={hub}
              profile={store.profile}
            />
          </div>
        )}{" "}
        {active === "resultado" && (
          <HistoryAccess name={text(store.visitor.name)} />
        )}{" "}
        {active === "avaliacao" && <FeedbackRequest initialWhatsapp={developmentAccess?.whatsapp} initialPin={developmentAccess?.pin} />}{" "}
        {active === "avaliacao" && developmentAccess && <FeedbackComparison name={text(store.visitor.name)} whatsapp={developmentAccess.whatsapp} pin={developmentAccess.pin} selfReviews={hub.selfReviews || []} reviews={hub.reviews || []} onSaved={loadHub} />}{" "}
        {active === "inicio" && (
          <AccessPin
            name={text(store.visitor.name)}
            whatsapp={text(store.visitor.whatsapp)}
            enabled={access && store.visitor.pinReady !== true}
            onReady={() =>
              openInventoryAfterRegistration(
                "Senha criada. Vamos começar o Inventário de Dons.",
              )
            }
          />
        )}{" "}
        {[
          "livros",
          "curriculo",
          "redes",
          "ecos",
          "editora",
          "feedback",
        ].includes(active) && <InstitutionalActions tab={active} />}{" "}
        {active === "comunidade" && (
          <div className="pdf-actions">
            <CommunityPdf community={community} />
          </div>
        )}{" "}
        {institutional}
      </main>
    </div>
  );
}
